import useMobileEvaluation from '@/hooks/useMobileEvaluation';
import MobileApplicantList from '../incubator/MobileApplicantList';
import { getEvalQuestions, getCumulativeScore } from './programEvaluation';
import IncubatorProgramCards from '../incubator/IncubatorProgramCards';
import { PROGRAM_COVER_SECTIONS } from '@/utils/programCoverSections';
import { createPortal } from 'react-dom';
import { ProgramPreviewModal } from '@/components/ProgramBuilderPreview';
import { ProgramCanvasCover, ProgramQuestionSummary } from '@/components/ProgramCanvasFields';
import { ProgramPublicFrame } from '@/components/ProgramApplySplit';
import { PROGRAM_THEMES, PROGRAM_COVERS } from '@/utils/programThemes';
import './program-builder-workspace.css';
import './program-builder-incubator-theme.css';
import '../incubator/incubator-programs-refined.css';
import './program-pipeline-toolbar.css';
import '../incubator/incubator-evaluation.css';
import '../incubator/evaluation-mobile.css';
import StartupPageTitle from '../../components/StartupPageTitle';
import AnimatedPipelineList from '../../components/AnimatedPipelineList';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api, { fileUrl } from '@/api';
import { useCurrency } from '@/hooks/useCurrency';
import { toast } from 'sonner';
import { Plus, Link, Copy, Trash, PencilSimple, Users, Buildings, X, EnvelopeSimple, MagnifyingGlass, ChartLine, Globe, CaretLeft, CaretRight, DotsSixVertical, Gear, Check, Sparkle, FilePdf, FileArrowDown, UploadSimple, Vault, Rows, Paperclip, Image, Eye, ChatCircleDots, DownloadSimple, Info, FunnelSimple, ArrowsDownUp, CaretUp, CaretDown, DotsThree, SquaresFour, List, ArrowLeft, ArrowRight, Tray, CheckCircle, Clock, XCircle } from '@phosphor-icons/react';

import { DndContext, useDraggable, useDroppable, PointerSensor, useSensor, useSensors, KeyboardSensor, DragOverlay, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { RichTextEditor, InlineRichEditor, stripHtml } from '@/components/RichTextEditor';
import {
  CloseEmailComposer,
  CloseEmailPreview,
  SendDecisionEmailsModal,
  useCloseEmailDrafts,
  toEmailPayload,
  CLOSE_EMAIL_CATEGORIES,
} from '@/components/CloseEmailComposer';
import { toDatetimeLocalValue, datetimeLocalToUTC, nowDatetimeLocalValue, formatDateTime, timeAgo } from '@/utils/datetime';
import { GO_NEXT, GO_SUBMIT, OTHER_LABEL_DEFAULT } from '@/utils/programForm';

import BulkUploadPrograms from './BulkUploadPrograms';
import ImportPortfolioDialog from './ImportPortfolioDialog';
import ClassicBulkUploadPrograms from './ClassicBulkUploadPrograms';
import StartupBadges, { STARTUP_BADGES } from '../../components/StartupBadges';

// Show imported profile progress consistently across application views.
const ImportedProfileBadge = ({ app }) => <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-semibold tracking-wide flex-shrink-0 ${app.profile_state === 'Needs review' ? 'bg-warning/10 text-warning' : app.profile_state === 'Profile completed' ? 'bg-success/10 text-success' : 'bg-primary/10 text-primary'}`} data-testid={`import-profile-state-${app.id}`}>{app.profile_state || 'Imported'}</span>;

// Stable ids let answer-based branching target sections before save.
const newId = () => (typeof crypto !== 'undefined' && crypto.randomUUID)
  ? crypto.randomUUID()
  : `q_${Math.random().toString(36).slice(2)}${Date.now()}`;

const QUESTION_TYPES = [
  { value: 'short_answer', label: 'Short answer (50 chars)' },
  { value: 'pincode', label: 'Pincode / location (automatic lookup)' },
  { value: 'paragraph', label: 'Paragraph (1000 chars)' },
  { value: 'single_choice', label: 'Single choice' },
  { value: 'multi_choice', label: 'Multiple choice (checkboxes)' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'pitch_deck', label: 'Pitch deck upload (.pdf/.pptx)' },
  { value: 'financial_statement', label: 'Financial statement upload (.pdf/.xlsx)' },
  { value: 'file', label: 'File upload (any document)' },
  { value: 'image', label: 'Image upload (.png/.jpg)' },
];

// One-click add presets — pre-fills label + type so the program builder is a single click.
const QUESTION_PRESETS = [
  { key: 'pitch_deck', type: 'pitch_deck', label: 'Upload your pitch deck', icon: 'FilePdf' },
  { key: 'financials', type: 'financial_statement', label: 'Upload your latest financial statement', icon: 'FileXls' },
  { key: 'dropdown_stage', type: 'dropdown', label: 'Current stage', options: ['Idea', 'MVP', 'Pre-Revenue', 'Revenue', 'Growth', 'Scaling'], icon: 'CaretCircleDown' },
];

// Question blocks — a named group of related questions. Clicking the block in
// Quick Add opens a chooser so the VC picks exactly which fields to include.
const QUESTION_BLOCKS = [
  {
    key: 'startup_basic',
    label: 'Startup Basic Information',
    icon: Buildings,
    fields: [
      { key: 'startup_name', label: 'Name of the Startup', type: 'short_answer' },
      { key: 'registered_name', label: 'Registered Name of the Startup (If Registered)', type: 'short_answer' },
      { key: 'num_founders', label: 'Number of Founders', type: 'short_answer' },
      { key: 'other_founders', label: 'Other Founder Details: Name | Degree | Years of Relevant Experience | Email IDs | Phone Number', type: 'paragraph' },
    ],
  },
  {
    key: 'primary_founder',
    label: 'Primary Founder Details',
    icon: Users,
    fields: [
      { key: 'pf_name', label: 'Name', type: 'short_answer' },
      { key: 'pf_email', label: 'Email ID', type: 'short_answer' },
      { key: 'pf_phone', label: 'Phone Number', type: 'short_answer' },
      { key: 'pf_college', label: 'College', type: 'short_answer' },
      { key: 'pf_degree', label: 'Degree', type: 'short_answer' },
      { key: 'pf_experience', label: 'Years of Experience', type: 'short_answer' },
      { key: 'pf_companies', label: 'Companies Worked for', type: 'short_answer' },
      { key: 'pf_prev_startup', label: 'Any Previous Startup founded', type: 'short_answer' },
    ],
  },
  {
    key: 'startup_metrics',
    label: 'Startup Details',
    icon: ChartLine,
    fields: [
      { key: 'mrr', label: 'Monthly Recurring Revenue', type: 'short_answer' },
      { key: 'profitable', label: 'Profitable? - How much?', type: 'short_answer' },
      { key: 'burn_rate', label: 'Current Burn Rate', type: 'short_answer' },
      { key: 'employees', label: 'Number of Employees (Excluding the Founders)', type: 'short_answer' },
      { key: 'marketing_spend', label: 'Marketing Spends (Average Monthly)', type: 'short_answer' },
    ],
  },
  {
    key: 'marketing_content',
    label: 'Marketing And Content',
    icon: Globe,
    fields: [
      { key: 'website', label: 'Website Link', type: 'short_answer' },
      { key: 'instagram', label: 'Instagram Link', type: 'short_answer' },
      { key: 'linkedin', label: 'Linkedin Link', type: 'short_answer' },
      { key: 'facebook', label: 'Facebook Link', type: 'short_answer' },
      { key: 'x_link', label: 'X Link', type: 'short_answer' },
    ],
  },
  {
    key: 'documentation',
    label: 'Documentation',
    icon: FilePdf,
    // Document fields default to the most fitting file-upload type automatically.
    fields: [
      { key: 'doc_pitch_deck', label: 'Pitch Deck', type: 'pitch_deck' },
      { key: 'doc_one_pager', label: 'One Pager', type: 'file' },
      { key: 'doc_audited_financials', label: 'Audited Financials', type: 'financial_statement' },
      { key: 'doc_mis', label: 'MIS', type: 'financial_statement' },
      { key: 'doc_financial_model', label: 'Financial Model', type: 'financial_statement' },
    ],
  },
];

const STAGES = [
  { key: 'received', label: 'Application Received', kanbanLabel: 'Received', short: 'RECEIVED', color: 'border-info/30 bg-info/5', text: 'text-info', dot: 'bg-info', headerGradient: 'from-info/30 via-info/10 to-transparent' },
  { key: 'accepted', label: 'Accepted', kanbanLabel: 'Accepted', short: 'ACCEPTED', color: 'border-success/30 bg-success/5', text: 'text-success', dot: 'bg-success', headerGradient: 'from-success/30 via-success/10 to-transparent' },
  { key: 'waitlisted', label: 'Waitlisted', kanbanLabel: 'Waitlisted', short: 'WAITLIST', color: 'border-warning/30 bg-warning/5', text: 'text-warning', dot: 'bg-warning', headerGradient: 'from-warning/30 via-warning/10 to-transparent' },
  { key: 'rejected', label: 'Rejected', kanbanLabel: 'Rejected', short: 'REJECTED', color: 'border-danger/30 bg-danger/5', text: 'text-danger', dot: 'bg-danger', headerGradient: 'from-danger/30 via-danger/10 to-transparent' },
];

// Manual evaluation ("Marking Score") is per-question — `evaluation_required` is a
// footer toggle next to "Required" on each question, and is scored right on that
// question's answer card. Every fund team member scores independently, so the
// application's evaluation_scores is a {question_id: {judge_user_id: 1-10}} map
// (a bare number under a question_id is the pre-multi-judge legacy shape, treated
// below as a "legacy" pseudo-judge so old data isn't dropped). The cumulative score
// shown in the Marking Score section averages each judge's own scores across
// questions, then averages those per-judge averages — always on the same 1-10
// scale regardless of question or judge count.
// ── Application pipeline filter + sort (shared by board + table) ──
// Keep the underlying application data untouched; these controls only decide
// what is rendered and in which order.
const appScores = (app, evalQuestions) => {
  const ai = app.ai_fit_score ?? null;
  const manual = getCumulativeScore(app.evaluation_scores, evalQuestions)?.avg ?? null;
  const parts = [ai != null ? ai / 10 : null, manual].filter(v => v != null);
  const both = parts.length ? Math.round((parts.reduce((a, b) => a + b, 0) / parts.length) * 10) / 10 : null;
  return { ai, manual, both };
};

const SCORE_BY = [
  { key: 'ai',     label: 'AI fit',   thresholds: [50, 75], unit: '/100' },
  { key: 'manual', label: 'Manual',   thresholds: [5, 8],   unit: '/10' },
  { key: 'both',   label: 'Combined', thresholds: [5, 8],   unit: '/10' },
];
const DEFAULT_SCORE_VIEW = {
  by: 'ai',
  show: 'all',
  badge: 'all',
  age: 'all',
  stage: 'all',
  sort: 'default',
};

const filterViewActive = (v) => v.show !== 'all' || v.badge !== 'all' || v.age !== 'all' || v.stage !== 'all';
const sortViewActive = (v) => v.sort !== 'default' && v.sort !== 'none';
const activeFilterCount = (v) => [v.show, v.badge, v.age, v.stage].filter(x => x !== 'all').length;

const passesScoreFilter = (s, view) => {
  const value = s[view.by];
  if (view.show === 'scored') return view.by === 'both' ? (s.ai != null && s.manual != null) : value != null;
  if (view.show === 'unscored') return view.by === 'both' ? (s.ai == null && s.manual == null) : value == null;
  if (view.show.startsWith('min:')) return value != null && value >= Number(view.show.slice(4));
  return true;
};

const passesPipelineFilters = (app, stageKey, view) => {
  if (view.stage !== 'all' && stageKey !== view.stage) return false;
  if (!passesScoreFilter(app._scores, view)) return false;

  if (view.badge !== 'all' && !(app.startup?.badges || []).includes(view.badge)) return false;

  if (view.age !== 'all') {
    const days = Number(view.age);
    const submitted = app.submitted_at ? new Date(app.submitted_at).getTime() : 0;
    if (!submitted || submitted < Date.now() - days * 24 * 60 * 60 * 1000) return false;
  }
  return true;
};

const applicantName = (app) => (app.startup?.startup_name || app.imported_startup_name || app.applicant_name || app.applicant_email || '').toLowerCase();
const compareNullableNumber = (x, y, dir = 'desc') => {
  if (x == null && y == null) return 0;
  if (x == null) return 1;
  if (y == null) return -1;
  return dir === 'asc' ? x - y : y - x;
};

const applicantAddedAt = (app) => {
  const timestamp = Date.parse(app.submitted_at || app.created_at || '');
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const comparePipeline = (sort, hasManual) => (a, b) => {
  switch (sort) {
    case 'default':
    case 'none':
    case 'newest': return applicantAddedAt(b) - applicantAddedAt(a);
    case 'oldest': return (new Date(a.submitted_at || 0)) - (new Date(b.submitted_at || 0));
    case 'name_asc': return applicantName(a).localeCompare(applicantName(b));
    case 'name_desc': return applicantName(b).localeCompare(applicantName(a));
    case 'comments_desc': return (b.comments_count || 0) - (a.comments_count || 0);
    case 'ai_desc': return compareNullableNumber(a._scores.ai, b._scores.ai, 'desc');
    case 'ai_asc': return compareNullableNumber(a._scores.ai, b._scores.ai, 'asc');
    case 'manual_desc': return hasManual ? compareNullableNumber(a._scores.manual, b._scores.manual, 'desc') : 0;
    case 'manual_asc': return hasManual ? compareNullableNumber(a._scores.manual, b._scores.manual, 'asc') : 0;
    case 'both_desc': return hasManual ? compareNullableNumber(a._scores.both, b._scores.both, 'desc') : 0;
    case 'both_asc': return hasManual ? compareNullableNumber(a._scores.both, b._scores.both, 'asc') : 0;
    default: return 0;
  }
};

const PipelineFilterToolbar = ({ value, onChange, hasManual, shown, total, onClose }) => {
  const by = SCORE_BY.find(b => b.key === value.by) || SCORE_BY[0];
  const set = (patch) => onChange({ ...value, ...patch });
  const selectCls = 'h-8 min-w-0 w-full max-w-full flex-1 bg-transparent border-0 px-1.5 pr-6 text-[11px] font-medium text-text-primary focus:outline-none focus:ring-0 cursor-pointer';
  const itemCls = 'flex items-center gap-1.5 h-8 min-w-0 overflow-hidden px-2 rounded-md border border-border/70 bg-transparent';
  return (
    <div className="mb-2 flex flex-wrap items-center gap-1.5 px-1 py-1.5 border-b border-border" data-testid="pipeline-filter-toolbar">
      <div className={`${itemCls} w-[200px] max-w-full`}>
        <span className="shrink-0 text-[9px] uppercase tracking-wider font-bold text-text-muted">Status</span>
        <select value={value.stage} onChange={e => set({ stage: e.target.value })} className={selectCls} data-testid="pipeline-filter-stage">
          <option value="all">All</option>
          {STAGES.map(st => <option key={st.key} value={st.key}>{st.label}</option>)}
        </select>
      </div>
      <div className={`${itemCls} w-[210px] max-w-full`}>
        <span className="shrink-0 text-[9px] uppercase tracking-wider font-bold text-text-muted">Badge</span>
        <Select value={value.badge} onValueChange={badge => set({ badge })}>
          <SelectTrigger aria-label="Filter by badge" className="h-8 min-w-0 flex-1 border-0 shadow-none px-1.5 text-[11px] font-medium text-text-primary focus:ring-0" data-testid="pipeline-filter-badge">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="incubator-program-select bg-surface text-text-primary border-border max-w-[calc(100vw-24px)]" collisionPadding={12}>
            <SelectItem value="all" className="text-[11px] focus:bg-primary/5">All badges</SelectItem>
            {STARTUP_BADGES.map(badge => (
              <SelectItem key={badge.id} value={badge.id} textValue={badge.label} className="focus:bg-primary/5" data-testid={`pipeline-filter-badge-${badge.id}`}>
                <StartupBadges badges={[badge.id]} size="xs" />
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className={`${itemCls} w-[145px] max-w-full`}>
        <span className="shrink-0 text-[9px] uppercase tracking-wider font-bold text-text-muted">Applied</span>
        <select value={value.age} onChange={e => set({ age: e.target.value })} className={selectCls} data-testid="pipeline-filter-age">
          <option value="all">Any time</option>
          <option value="7">7 days</option>
          <option value="30">30 days</option>
          <option value="90">90 days</option>
        </select>
      </div>
      <div className={`${itemCls} w-[140px] max-w-full`}>
        <span className="shrink-0 text-[9px] uppercase tracking-wider font-bold text-text-muted">Score</span>
        <select value={value.show} onChange={e => set({ show: e.target.value })} className={selectCls} data-testid="pipeline-filter-score">
          <option value="all">Any</option>
          <option value="scored">Scored</option>
          <option value="unscored">Unscored</option>
          {by.thresholds.map(t => <option key={t} value={`min:${t}`}>{t}+{by.unit}</option>)}
        </select>
      </div>
      <div className="flex items-center h-8 rounded-md border border-border/70 bg-transparent overflow-hidden">
        <span className="pl-2 pr-1 text-[9px] uppercase tracking-wider font-bold text-text-muted">Using</span>
        {SCORE_BY.map(b => {
          const disabled = !hasManual && b.key !== 'ai';
          return <button key={b.key} type="button" disabled={disabled}
            onClick={() => set({ by: b.key, show: value.show.startsWith('min:') ? 'all' : value.show })}
            className={`h-8 px-2 text-[10px] font-semibold transition-colors disabled:opacity-30 ${value.by === b.key ? 'text-primary bg-primary/10' : 'text-text-muted hover:text-text-primary'}`}
            data-testid={`pipeline-score-by-${b.key}`}>{b.label}</button>;
        })}
      </div>
      <div className="ml-auto flex items-center gap-2 h-8 pl-1 text-[10px] text-text-muted">
        <span><strong className="text-text-primary">{shown}</strong>/{total}</span>
        <button type="button" onClick={onClose} className="w-7 h-7 rounded-md flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-primary/5" title="Hide filters"><X size={11} weight="bold" /></button>
      </div>
    </div>
  );
};

const PipelineSortToolbar = ({ value, onChange, hasManual, onClose }) => {
  const options = [
    { key: 'default', label: 'Default' },
    { key: 'newest', label: 'Newest' },
    { key: 'oldest', label: 'Oldest' },
    { key: 'name_asc', label: 'A → Z' },
    { key: 'comments_desc', label: 'Most discussed' },
    { key: 'ai_desc', label: 'AI fit high' },
    { key: 'ai_asc', label: 'AI fit low' },
    ...(hasManual ? [
      { key: 'manual_desc', label: 'Manual score' },
      { key: 'both_desc', label: 'Combined score' },
    ] : []),
  ];
  return (
    <div className="mb-2 flex items-center gap-1 px-1 py-1.5 border-b border-border overflow-x-auto" data-testid="pipeline-sort-toolbar">
      <span className="flex-shrink-0 px-2 text-[9px] uppercase tracking-wider font-bold text-text-muted">Sort by</span>
      {options.map(opt => {
        const active = value.sort === opt.key || (opt.key === 'default' && value.sort === 'none');
        return (
          <button key={opt.key} type="button" onClick={() => onChange({ ...value, sort: opt.key })}
            className={`flex-shrink-0 h-8 px-2.5 rounded-md text-[11px] font-semibold transition-colors ${active ? 'bg-primary/10 text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-primary/5'}`}
            data-testid={`pipeline-sort-${opt.key}`}>
            {opt.label}
          </button>
        );
      })}
      <button type="button" onClick={onClose} className="ml-auto flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-primary/5" title="Hide sorting"><X size={11} weight="bold" /></button>
    </div>
  );
};

// Cumulative pill — average of every judge's own average, out of 10. Tone ramps
// from danger (low) to success (high), same idea as FitBadge.
const CumulativeScoreBadge = ({ summary, size = 'xs' }) => {
  if (!summary || summary.avg == null) return null;
  const tone = summary.avg >= 8 ? 'bg-success/10 text-success' : summary.avg >= 5 ? 'bg-warning/10 text-warning' : 'bg-danger/10 text-danger';
  const sizeCls = size === 'sm' ? 'text-[11px] px-2 py-1 gap-1' : 'text-[9px] px-1.5 py-0.5 gap-0.5';
  const judgeSuffix = summary.judgeCount ? ` · ${summary.judgeCount} judge${summary.judgeCount === 1 ? '' : 's'}` : '';
  return (
    <span className={`inline-flex items-center rounded-full font-semibold flex-shrink-0 ${tone} ${sizeCls}`}
      title={`${summary.scoredCount}/${summary.totalCount} questions scored${judgeSuffix}`} data-testid="cumulative-score-badge">
      {summary.avg}/10
    </span>
  );
};

const Toggle = ({ checked, onChange, testId, label }) => (
  <button
    type="button"
    role="switch"
    aria-label={label}
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    data-testid={testId}
    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none ${checked ? 'bg-primary' : 'bg-text-muted/30'}`}
  >
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
  </button>
);

// Sortable wrapper for a question/section block. Render-prop hands the child a
// ref (for the block), a transform style, and the drag-handle props (spread on
// the 6-dot handle only, so typing inside the block never starts a drag).
const SortableBlock = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 20 : 'auto',
    position: 'relative',
  };
  return children({ setNodeRef, style, handleProps: { ...attributes, ...listeners } });
};

export const ProgramFormModal = ({ initial, onSave, onClose, isIncubator }) => {
  const [form, setForm] = useState(() => ({
    name: initial?.name || '',
    description: initial?.description || '',
    hero_text: initial?.hero_text || '',
    cover_sections: initial?.cover_sections || [],
    status: initial?.status || 'open',
    scheduled_open_at: initial?.scheduled_open_at ? toDatetimeLocalValue(initial.scheduled_open_at) : '',
    open_until: initial?.open_until ? toDatetimeLocalValue(initial.open_until) : '',
    cover_images: initial?.cover_images || (isIncubator && !initial ? ['builtin:charcoal'] : []),
    theme: initial?.theme || (isIncubator && !initial ? 'studio' : null),
    ai_eval_criteria: initial?.ai_eval_criteria || '',
    // Migrate legacy question types (from before the short-answer/paragraph consolidation)
    // so every question maps to a current type option: 'text'/'long_answer' → 'paragraph',
    // 'text_box' → 'short_answer' (same single-line input, just a smaller cap now).
    questions: initial?.questions?.length
      ? initial.questions.map(q => ({
          ...q,
          id: q.id || newId(),
          type: (q.type === 'text' || q.type === 'long_answer') ? 'paragraph' : q.type === 'text_box' ? 'short_answer' : q.type,
        }))
      : [{ id: newId(), type: 'short_answer', label: '', required: true, options: [] }],
  }));
  const [builderTab, setBuilderTab] = useState('canvas');
  const [visiblePanel, setVisiblePanel] = useState(null);
  useEffect(() => {
    const next = ['design', 'publish', 'add'].includes(builderTab) ? builderTab : null;
    if (next) { setVisiblePanel(next); return; }
    if (!visiblePanel) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setVisiblePanel(null); return; }
    const timer = window.setTimeout(() => setVisiblePanel(null), 160);
    return () => window.clearTimeout(timer);
  }, [builderTab, visiblePanel]);
  const [activeQuestion, setActiveQuestion] = useState(() => form.questions.find(q => !stripHtml(q.label))?.id || null);
  useEffect(() => {
    if (!activeQuestion || builderTab !== 'canvas') return;
    const node = workspaceRef.current?.querySelector(`[data-question-id="${activeQuestion}"]`);
    node?.scrollIntoView?.({ block: 'nearest', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    node?.querySelector('[contenteditable="true"]')?.focus({ preventScroll: true });
  }, [activeQuestion, builderTab]);
  const [fund, setFund] = useState(null);
  useEffect(() => { if (isIncubator) api.get('/vc/fund').then(({data}) => setFund(data)).catch(() => {}); }, [isIncubator]);
  const [canvasPage, setCanvasPage] = useState('cover');
  const [showDiscard, setShowDiscard] = useState(false);
  const editorRef = useRef(null);
  const workspaceRef = useRef(null);
  const snapshot = useRef(JSON.stringify(form));
  const hasChanges = JSON.stringify(form) !== snapshot.current;
  const closeBuilder = () => {
    if (saving) return;
    if (hasChanges) { setShowDiscard(true); return; }
    onClose();
  };
  useEffect(() => {
    if (!isIncubator) return;
    const previous = document.body.style.overflow;
    const previousBuilder = document.body.getAttribute('data-program-builder-open');
    document.body.style.overflow = 'hidden';
    document.body.setAttribute('data-program-builder-open', 'true');
    return () => { document.body.style.overflow = previous; if (previousBuilder === null) document.body.removeAttribute('data-program-builder-open'); else document.body.setAttribute('data-program-builder-open', previousBuilder); };
  }, [isIncubator]);
  useEffect(() => {
    editorRef.current?.scrollTo?.({ top: 0 });
    if (!isIncubator || !['design', 'publish', 'add'].includes(builderTab)) return;
    const previous = document.activeElement;
    return () => { if (previous?.isConnected) previous.focus?.(); };
  }, [builderTab, isIncubator]);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  useEffect(() => {
    if (!isIncubator || !hasChanges) return;
    const preventExit = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', preventExit);
    return () => window.removeEventListener('beforeunload', preventExit);
  }, [hasChanges, isIncubator]);
  useEffect(() => {
    if (!isIncubator) return;
    const previous = document.activeElement;
    workspaceRef.current?.querySelector('[data-testid="program-name-input"]')?.focus();
    return () => previous?.focus?.();
  }, [isIncubator]);
  useEffect(() => {
    if (!isIncubator || showPreview) return;
    const onKey = event => {
      if (event.defaultPrevented) return;
      if (showDiscard) { if (event.key === 'Escape') setShowDiscard(false); return; }
      if (blockPicker) return;
      if (event.key === 'Escape' && builderTab !== 'canvas') { event.preventDefault(); setBuilderTab('canvas'); return; }
      if (event.key === 'Escape') { event.preventDefault(); closeBuilder(); }
      if (event.key === 'Tab') {
        const focusScope = workspaceRef.current?.querySelector('.pb-settings-backdrop:not([data-state="closing"]) .pb-canvas-settings') || workspaceRef.current;
        const nodes = [...(focusScope?.querySelectorAll('button:not(:disabled),input:not(:disabled),textarea,select,[tabindex="0"],[contenteditable="true"]') || [])].filter(node => node.getClientRects().length);
        const first = nodes[0], last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });


  const uploadCoverImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error('Image must be under 8MB'); return; }
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, cover_images: [...prev.cover_images, res.data.file_id] }));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to upload image'); }
    finally { setUploadingCover(false); }
  };
  const removeCoverImage = (idx) => setForm(prev => ({ ...prev, cover_images: prev.cover_images.filter((_, i) => i !== idx) }));

  const updateQ = (idx, patch) => { const next = [...form.questions]; next[idx] = { ...next[idx], ...patch }; setForm({ ...form, questions: next }); };
  const appendQuestions = questions => {
    setForm(prev => ({ ...prev, questions: [...prev.questions, ...questions] }));
    if (isIncubator) { setActiveQuestion(questions[0]?.id || null); setCanvasPage('form'); setBuilderTab('canvas'); }
  };
  const addQ = () => appendQuestions([{ id: newId(), type: 'short_answer', label: '', required: false, options: [] }]);
  const addSection = () => appendQuestions([{ id: newId(), type: 'section', label: '', required: false, options: null }]);
  const addFile = () => appendQuestions([{ id: newId(), type: 'file', label: '', required: false, options: null }]);
  const addImage = () => appendQuestions([{ id: newId(), type: 'image', label: '', required: false, options: null }]);
  const removeQ = (idx) => setForm({ ...form, questions: form.questions.filter((_, i) => i !== idx) });
  const dupQ = (idx) => {
    const copyId = newId();
    setForm(prev => {
    const q = prev.questions[idx];
    const copy = { ...q, id: copyId, options: Array.isArray(q.options) ? [...q.options] : q.options, option_targets: Array.isArray(q.option_targets) ? [...q.option_targets] : q.option_targets };
    const next = [...prev.questions];
    next.splice(idx + 1, 0, copy);
    return { ...prev, questions: next };
    });
    if (isIncubator) setActiveQuestion(copyId);
  };

  // Question-block chooser: pick which fields of a block to add.
  const [blockPicker, setBlockPicker] = useState(null);  // the block being configured
  const [blockSel, setBlockSel] = useState({});           // { fieldKey: bool }
  const openBlock = (block) => {
    setBlockPicker(block);
    setBlockSel(Object.fromEntries(block.fields.map(f => [f.key, true])));  // all selected by default
  };
  const addBlockSelected = () => {
    const chosen = blockPicker.fields.filter(f => blockSel[f.key]);
    if (chosen.length) {
      const newQs = chosen.map(f => ({
        id: newId(), type: f.type, label: f.label, required: false,
        options: (f.type === 'dropdown' || f.type === 'single_choice' || f.type === 'multi_choice') ? (f.options || []) : null,
      }));
      appendQuestions(newQs);
    }
    setBlockPicker(null);
  };
  const selectedCount = blockPicker ? blockPicker.fields.filter(f => blockSel[f.key]).length : 0;

  // Drag-to-reorder question/section blocks. Reordering the flat list also
  // reassigns a question to whichever section it now sits under (section
  // membership is positional), so editing sections works correctly too.
  const dragSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const handleQuestionDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setForm(prev => {
      const oldIndex = prev.questions.findIndex(q => q.id === active.id);
      const newIndex = prev.questions.findIndex(q => q.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return { ...prev, questions: arrayMove(prev.questions, oldIndex, newIndex) };
    });
  };

  const handleSave = async (overrideStatus) => {
    if (!form.name.trim()) { setBuilderTab('canvas'); setCanvasPage('cover'); toast.error('Program name is required'); return; }
    const finalStatus = overrideStatus || form.status;
    if (finalStatus === 'scheduled' && !form.scheduled_open_at) {
      setBuilderTab('publish'); toast.error('Pick a date & time for the program to open'); return;
    }
    if (finalStatus === 'open' && form.open_until && datetimeLocalToUTC(form.open_until) <= new Date().toISOString()) {
      toast.error('The close date must be in the future'); return;
    }
    if (finalStatus === 'scheduled' && form.open_until && datetimeLocalToUTC(form.open_until) <= datetimeLocalToUTC(form.scheduled_open_at)) { setBuilderTab('publish'); toast.error('Closing time must be after opening time'); return; }
    if (isIncubator && finalStatus !== 'draft') {
      const emptyQuestion = form.questions.find(q => !stripHtml(q.label));
      if (emptyQuestion) { setBuilderTab('canvas'); setActiveQuestion(emptyQuestion.id); setCanvasPage('form'); toast.error('Give every question and section a title, or remove empty cards.'); return; }
      const emptyChoice = form.questions.find(q => ['single_choice', 'multi_choice', 'dropdown'].includes(q.type) && !(q.options || []).some(option => option.trim()));
      if (emptyChoice) { setBuilderTab('canvas'); setActiveQuestion(emptyChoice.id); setCanvasPage('form'); toast.error('Add at least one answer option to each choice question.'); return; }
    }
    // Labels are rich HTML now — check emptiness on the plain-text content.
    const cleanedQs = form.questions.filter(q => stripHtml(q.label)).map(q => {
      q = { ...q, placeholder: (q.placeholder || '').trim() || null };
      if (q.type === 'single_choice' || q.type === 'multi_choice' || q.type === 'dropdown') {
        // Drop blank options and keep option_targets index-aligned with them.
        const kept = (q.options || []).map((o, i) => [o, i]).filter(([o]) => o && o.trim());
        // Branching needs one exclusive answer, so multi_choice can't drive it.
        const branches = q.type !== 'multi_choice' && !!q.branch_on_answer;
        // "Other" (free-text) is for pick-lists only. A blank custom label is
        // dropped so applicants see the default "Other".
        const other = q.type !== 'dropdown' && !!q.allow_other;
        return {
          ...q,
          options: kept.map(([o]) => o),
          branch_on_answer: branches,
          option_targets: branches ? kept.map(([, i]) => (q.option_targets || [])[i] || GO_NEXT) : null,
          allow_other: other,
          other_label: (other && (q.other_label || '').trim()) || null,
        };
      }
      return { ...q, options: null, branch_on_answer: false, option_targets: null, allow_other: false, other_label: null };
    });
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), description: form.description, hero_text: form.hero_text, cover_sections: form.cover_sections.map(section => ({...section, title: (section.title || '').trim(), body: (section.body || '').trim()})), status: finalStatus, questions: cleanedQs, theme: form.theme, cover_images: form.cover_images, ai_eval_criteria: form.ai_eval_criteria.trim() || null };
      if (finalStatus === 'scheduled') {
        // datetime-local is browser-local wall time → convert to a UTC ISO instant
        payload.scheduled_open_at = datetimeLocalToUTC(form.scheduled_open_at);
      } else {
        payload.scheduled_open_at = null;
      }
      // Optional auto-close deadline — only meaningful while the program is open.
      payload.open_until = (['open', 'scheduled'].includes(finalStatus) && form.open_until) ? datetimeLocalToUTC(form.open_until) : null;
      await onSave(payload);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  // Sections available as branch targets (answer-based "go to section").
  const sectionList = form.questions.filter(q => q.type === 'section');
  const sectionTitle = (s) => stripHtml(s.label) || 'Untitled section';

  const contentTools = (<><div className="pb-question-tools" aria-label="Add form content">{[[addQ,Plus,'Question','add-question-btn-inline'],[addFile,Paperclip,'File upload','add-file-btn-inline'],[addImage,Image,'Image upload','add-image-btn-inline'],[addSection,Rows,'Section','add-section-btn-inline']].map(([action,Icon,label,testId]) => <button type="button" key={testId} onClick={action} data-testid={testId}><Icon size={17} weight="bold"/><span>{label}</span></button>)}</div></>);
  const questionLibrary = (<><div className="pb-question-library" data-testid="question-presets">
              <div className="pb-library-heading"><strong>Question library</strong><span>Ready-made fields and groups</span></div>
              <div className="pb-library-label">Individual fields</div>
              <div className="pb-library-fields">{QUESTION_PRESETS.map(preset => <button type="button" key={preset.key} onClick={() => appendQuestions([{id:newId(),type:preset.type,label:preset.label,required:false,options:preset.options || null}])} data-testid={`add-preset-${preset.key}-btn`}><Plus size={13}/><span>{preset.key === 'pitch_deck' ? 'Pitch deck' : preset.key === 'financials' ? 'Financial statement' : 'Startup stage'}</span></button>)}</div>
              <div className="pb-library-label">Question groups</div>
              <div className="pb-library-blocks">{QUESTION_BLOCKS.map(block => {const Icon=block.icon;return <button type="button" key={block.key} onClick={() => openBlock(block)} data-testid={`add-block-${block.key}-btn`}><Icon size={16}/><span>{block.label}</span><CaretRight size={13}/></button>;})}</div>
            </div></>);
  const contentControls = (<>{contentTools}{questionLibrary}</>);
  const questionCanvas = (<>
          <div >
            <div hidden={isIncubator} className="flex items-center justify-between mb-2">
              <label className="text-[11px] uppercase tracking-wider text-text-secondary">Application Questions</label>
              {(() => { const n = form.questions.filter(q => q.type !== 'section').length; return (
                <span className="text-[11px] text-text-muted tabular-nums">{n} question{n === 1 ? '' : 's'}</span>
              ); })()}
            </div>
            <div hidden={isIncubator} className="flex items-start gap-2 text-[12px] text-text-secondary bg-info/10 border border-info/30 rounded-lg px-3 py-2 mb-3" data-testid="builtin-fields-disclaimer">
              <Info size={14} weight="bold" className="text-info flex-shrink-0 mt-0.5" />
              <span><strong className="font-bold text-text-primary">Name</strong>, <strong className="font-bold text-text-primary">email</strong> and <strong className="font-bold text-text-primary">phone number</strong> are collected automatically for every applicant — no need to add questions for them below.</span>
            </div>
            {isIncubator && <details className="pb-contact-details" open><summary><span><Users size={16}/><strong>Contact details</strong><small>Name, email & phone · always included</small></span><CaretDown size={14}/></summary><div className="pb-canvas-builtins" aria-label="Standard applicant fields"><label><strong>Your name *</strong><input disabled placeholder="Jane Founder" /></label><label><strong>Email *</strong><input disabled placeholder="you@startup.com" /></label><label><strong>Phone *</strong><input disabled placeholder="Phone number" /></label><small>Contact details · included in every application</small></div></details>}

            {!isIncubator && contentTools}
            <div className="space-y-4">
              <DndContext sensors={dragSensors} collisionDetection={closestCenter} onDragEnd={handleQuestionDragEnd}>
              <SortableContext items={form.questions.map(q => q.id)} strategy={verticalListSortingStrategy}>
              {form.questions.map((q, idx) => (
                <SortableBlock key={q.id} id={q.id}>
                {({ setNodeRef, style, handleProps }) => {
                const selected = !isIncubator || activeQuestion === q.id;
                const selectQuestion = () => setActiveQuestion(q.id);
                if (isIncubator && !selected) return <div ref={setNodeRef} style={style} className="pb-calm-question" data-question-id={q.id} data-testid={`question-summary-${idx}`}><ProgramQuestionSummary q={q} index={idx} typeLabel={QUESTION_TYPES.find(t => t.value === q.type)?.label || q.type} onEdit={selectQuestion} dragHandle={<button type="button" {...handleProps} title="Drag to reorder" aria-label="Drag to reorder" className="pb-summary-drag"><DotsSixVertical size={16}/></button>}/></div>;
                // Section divider — a grouping header, not an answerable question.
                if (q.type === 'section') {
                  return (
                    <div ref={setNodeRef} style={style} className="bg-primary/5 border border-primary/30 border-l-4 border-l-primary rounded-xl pb-question-selected" data-question-id={q.id} data-testid={`section-card-${idx}`}>
                      <div className="flex items-center justify-between px-4 pt-2.5">
                        <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-primary">Section</span>
                        <button type="button" {...handleProps} title="Drag to reorder" data-testid={`section-drag-${idx}`}
                          className="cursor-grab active:cursor-grabbing touch-none text-text-muted/50 hover:text-text-muted">
                          <DotsSixVertical size={16} weight="bold" className="rotate-90" />
                        </button>
                      </div>
                      <div className="px-4 py-2">
                        <InlineRichEditor value={q.label} onChange={(html) => updateQ(idx, { label: html })} placeholder="Section title" data-testid={`section-title-${idx}`} />
                        <p className="text-[11px] text-text-muted mt-1.5">Questions below this appear grouped under this section on the application.</p>
                      </div>
                      <div className="flex items-center justify-end gap-3 px-4 py-2.5 border-t border-primary/20">{isIncubator && <button type="button" className="pb-question-done" onClick={() => setActiveQuestion(null)}>Done <Check size={13}/></button>}
                        <button type="button" onClick={() => dupQ(idx)} title="Duplicate" data-testid={`section-dup-${idx}`} className="text-text-muted hover:text-text-primary"><Copy size={16} /></button>
                        <button type="button" onClick={() => removeQ(idx)} title="Delete" data-testid={`section-remove-${idx}`} className="text-text-muted hover:text-danger"><Trash size={16} /></button>
                      </div>
                    </div>
                  );
                }
                const isChoice = q.type === 'single_choice' || q.type === 'multi_choice' || q.type === 'dropdown';
                // Only exclusive answers can drive branching, and only pick-lists get "Other".
                const isBranchable = q.type === 'single_choice' || q.type === 'dropdown';
                const allowsOther = q.type === 'single_choice' || q.type === 'multi_choice';
                // Option bullet: circle for radios, square for checkboxes, index for dropdowns.
                const optionMark = (dashed) => q.type === 'single_choice'
                  ? <span className={`w-4 h-4 rounded-full border-2 ${dashed ? 'border-dashed border-text-muted/30' : 'border-text-muted/40'} shrink-0`} />
                  : q.type === 'multi_choice'
                    ? <span className={`w-4 h-4 rounded-[4px] border-2 ${dashed ? 'border-dashed border-text-muted/30' : 'border-text-muted/40'} shrink-0`} />
                    : null;
                const isUpload = q.type === 'pitch_deck' || q.type === 'financial_statement' || q.type === 'file' || q.type === 'image';
                const uploadAccept = q.type === 'pitch_deck' ? '.pdf, .pptx, .ppt'
                  : q.type === 'financial_statement' ? '.pdf, .xlsx, .xls, .csv'
                  : q.type === 'image' ? '.png, .jpg, .jpeg, .webp'
                  : '.pdf, .pptx, .ppt, .xlsx, .xls, .csv';
                return (
                <div ref={setNodeRef} style={style} className={`bg-surface border border-border rounded-xl shadow-sm ${isIncubator ? 'pb-question-selected' : ''}`} data-question-id={q.id} data-testid={`question-card-${idx}`}>
                  {/* Drag-handle strip (Google-Forms style header) */}
                  <div className="flex justify-center py-1 select-none">
                    <button type="button" {...handleProps} title="Drag to reorder" data-testid={`question-drag-${idx}`}
                      className="cursor-grab active:cursor-grabbing touch-none text-text-muted/40 hover:text-text-muted px-6">
                      <DotsSixVertical size={16} weight="bold" className="rotate-90" />
                    </button>
                  </div>

                  {/* Row 1 — question text (rich: bold/italic/strike/link) first, then the question type */}
                  <div className="flex flex-col sm:flex-row gap-3 px-4">
                    <div className="flex-1">
                      <InlineRichEditor value={q.label} onChange={(html) => updateQ(idx, { label: html })} placeholder="Question" data-testid={`question-label-${idx}`} />
                    </div>
                    <select value={q.type} onChange={e => {
                      const t = e.target.value;
                      const choice = t === 'single_choice' || t === 'multi_choice' || t === 'dropdown';
                      const keepsOther = t === 'single_choice' || t === 'multi_choice';
                      updateQ(idx, {
                        type: t,
                        options: choice ? (q.options?.length ? q.options : ['']) : null,
                        allow_other: keepsOther ? !!q.allow_other : false,
                        other_label: keepsOther ? q.other_label : null,
                        // Checkbox answers can't route sections — drop any branching set up earlier.
                        ...(t === 'multi_choice' ? { branch_on_answer: false, option_targets: null } : {}),
                      });
                    }}
                      data-testid={`question-type-${idx}`}
                      className="sm:w-56 self-start bg-surface border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary cursor-pointer">
                      {QUESTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>

                  {/* Body — options / upload hint / text preview */}
                  <div className="px-4 pt-3">
                    {isChoice && (
                      <div className="space-y-2">
                        {(q.options || []).map((opt, oi) => (
                          <div key={oi} className="flex items-center gap-2.5 flex-wrap" data-testid={`question-${idx}-option-${oi}`}>
                            {optionMark(false) || <span className="text-xs text-text-muted w-4 text-center shrink-0">{oi + 1}.</span>}
                            <input type="text" value={opt} onChange={e => { const next = [...q.options]; next[oi] = e.target.value; updateQ(idx, { options: next }); }}
                              placeholder={`Option ${oi + 1}`}
                              className="flex-1 min-w-[120px] bg-transparent border-b border-transparent hover:border-border focus:border-primary px-1 py-1 text-sm text-text-primary focus:outline-none transition-colors" />
                            {q.branch_on_answer && (
                              <select
                                value={(q.option_targets || [])[oi] || GO_NEXT}
                                onChange={e => { const t = [...(q.option_targets || [])]; while (t.length < (q.options || []).length) t.push(GO_NEXT); t[oi] = e.target.value; updateQ(idx, { option_targets: t }); }}
                                data-testid={`question-${idx}-option-${oi}-target`}
                                title="Go to section based on this answer"
                                className="text-[11px] bg-surface border border-primary/30 rounded px-2 py-1 text-text-primary focus:outline-none focus:border-primary cursor-pointer max-w-[180px]">
                                <option value={GO_NEXT}>→ Continue to next section</option>
                                {sectionList.map(s => <option key={s.id} value={s.id}>→ Go to “{sectionTitle(s)}”</option>)}
                                <option value={GO_SUBMIT}>→ Submit form</option>
                              </select>
                            )}
                            <button onClick={() => updateQ(idx, { options: q.options.filter((_, i) => i !== oi), option_targets: (q.option_targets || []).filter((_, i) => i !== oi) })} className="text-text-muted hover:text-danger shrink-0"><X size={14} /></button>
                          </div>
                        ))}
                        {/* "Other" — applicants pick it and type their own answer. Choice lists only.
                            The label defaults to "Other" but is editable (e.g. "Something else"). */}
                        {allowsOther && q.allow_other && (
                          <div className="flex items-center gap-2.5" data-testid={`question-${idx}-other-option`}>
                            {optionMark(false)}
                            <input type="text" value={q.other_label ?? ''} onChange={e => updateQ(idx, { other_label: e.target.value })}
                              placeholder={OTHER_LABEL_DEFAULT} maxLength={60} title="Label for the free-text choice"
                              data-testid={`question-${idx}-other-label`}
                              className="w-32 shrink-0 bg-transparent border-b border-transparent hover:border-border focus:border-primary px-1 py-1 text-sm text-text-primary focus:outline-none transition-colors" />
                            <span className="text-sm text-text-primary shrink-0 -ml-1.5">:</span>
                            <span className="flex-1 min-w-[120px] border-b border-dashed border-border/70 px-1 py-1 text-sm text-text-muted/50">Applicant types their answer</span>
                            <button onClick={() => updateQ(idx, { allow_other: false })} title='Remove "Other"' className="text-text-muted hover:text-danger shrink-0"><X size={14} /></button>
                          </div>
                        )}
                        <div className="flex items-center gap-2 flex-wrap pt-0.5">
                          <button onClick={() => updateQ(idx, { options: [...(q.options || []), ''], option_targets: q.branch_on_answer ? [...(q.option_targets || []), GO_NEXT] : q.option_targets })} data-testid={`question-add-option-${idx}`}
                            className="flex items-center gap-2.5 text-sm text-text-muted hover:text-primary">
                            {optionMark(true) || <span className="w-4 text-center shrink-0">+</span>}
                            Add option
                          </button>
                          {allowsOther && !q.allow_other && (
                            <>
                              <span className="text-sm text-text-muted">or</span>
                              <button onClick={() => updateQ(idx, { allow_other: true })} data-testid={`question-add-other-${idx}`}
                                className="text-sm text-primary hover:underline">add "Other"</button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    {isUpload && (
                      <div className="flex items-center gap-2 text-[12px] text-text-secondary bg-background border border-dashed border-border rounded-lg px-3 py-2.5">
                        <UploadSimple size={14} weight="bold" className="shrink-0 text-text-muted" />
                        Applicants see a file-upload widget · {uploadAccept} · max 25 MB
                      </div>
                    )}
                    {(q.type === 'short_answer' || q.type === 'paragraph') && (
                      <div>
                        <input type="text" value={q.placeholder || ''} onChange={e => updateQ(idx, { placeholder: e.target.value })}
                          placeholder="Answer hint (optional)" maxLength={120}
                          data-testid={`question-${idx}-placeholder`}
                          className="w-full bg-transparent border-b border-transparent hover:border-border focus:border-primary px-1 py-1 text-sm text-text-primary focus:outline-none transition-colors" />
                        <div className="pb-answer-help text-[10px] text-text-muted/70 mt-1">
                          {q.type === 'pincode' ? 'Pincode · automatic city and state lookup' : q.type === 'short_answer' ? 'Short answer · up to 50 characters' : 'Paragraph · up to 1000 characters'} · hint disappears when the applicant types.
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="pb-question-routing">{isBranchable && (
                      <label className="flex items-center gap-2 mr-auto text-[13px] text-text-secondary select-none cursor-pointer" title="Send applicants to a specific section depending on their answer">
                        Go to section based on answer
                        <Toggle
                          checked={!!q.branch_on_answer}
                          onChange={(v) => updateQ(idx, { branch_on_answer: v, option_targets: v ? (q.options || []).map((_, i) => (q.option_targets || [])[i] || GO_NEXT) : (q.option_targets || null) })}
                          testId={`question-branch-${idx}`}
                        />
                      </label>
                    )}</div>

                  {/* Footer — branching (single-answer choices only) · duplicate / delete · Required / Evaluation required toggles */}
                  <div className="flex items-center justify-end gap-3 px-4 py-3 mt-3 border-t border-border flex-wrap">{isIncubator && <button type="button" className="pb-question-done" onClick={() => setActiveQuestion(null)}>Done <Check size={13}/></button>}
                    <button type="button" onClick={() => dupQ(idx)} title="Duplicate" data-testid={`question-dup-${idx}`} className="text-text-muted hover:text-text-primary"><Copy size={16} /></button>
                    <button type="button" onClick={() => removeQ(idx)} title="Delete" data-testid={`question-remove-${idx}`} className="text-text-muted hover:text-danger"><Trash size={16} /></button>
                    {isIncubator && <label className="pb-question-score" title="Your team scores this answer 1–10 once applications are received"><span>Marking Score</span><Toggle checked={!!q.evaluation_required} onChange={v => updateQ(idx, { evaluation_required: v })} testId={`question-evaluation-required-${idx}`} label="Marking Score"/></label>}
                    <span className="w-px h-5 bg-border" />
                    <span className="text-[13px] text-text-secondary select-none">Required</span>
                    <Toggle checked={!!q.required} onChange={(v) => updateQ(idx, { required: v })} testId={`question-required-${idx}`} />
                  </div>
                </div>
              );
                }}
                </SortableBlock>
              ))}
              </SortableContext>
              </DndContext>
            </div>

            {isIncubator && <button type="button" className="pb-add-content" onClick={() => setBuilderTab('add')}><Plus size={17}/>Add question or section</button>}
            {!isIncubator && questionLibrary}

          </div>

</>);
  const designControls = (<>
          <div  className="space-y-6 pb-design-panel">
            {isIncubator && <><div className="pb-section-title"><span className="pb-eyebrow">YOUR PROGRAM, YOUR IDENTITY</span><h4>Give it a look of its own</h4><p>Choose a theme and cover. These are saved with your program and appear on the published application page.</p></div>
            <div><div className="pb-field-heading">Page theme</div><div className="pb-theme-grid">{PROGRAM_THEMES.filter(theme => theme.id !== 'climate').map(theme => <button type="button" className="pb-theme-card" key={theme.id} aria-pressed={form.theme === theme.id} onClick={() => setForm(prev => ({...prev, theme: theme.id}))}><div className="pb-theme-sample" style={{background:theme.background}}><div style={{background:theme.accent}}/><div style={{background:theme.surface}}><i style={{background:theme.ink}}/><i style={{background:theme.border}}/><i style={{background:theme.border}}/><b style={{background:theme.accent}}/></div></div><strong>{theme.name}{form.theme === theme.id && <Check size={13} weight="bold" />}</strong><small>{theme.description}</small></button>)}</div>{!form.theme && <p className="pb-help">This program currently uses the original theme. Choose a preset to update it.</p>}</div>
            <div><div className="pb-field-heading">Cover collection <span>Included with FounderBoard</span></div><div className="pb-cover-grid">{PROGRAM_COVERS.filter(cover => cover.id !== 'builtin:charcoal').map(cover => <button type="button" key={cover.id} aria-pressed={form.cover_images.includes(cover.id)} className="pb-cover-card" onClick={() => setForm(prev => ({...prev, cover_images: [cover.id]}))}><img src={cover.src} alt={cover.name} /><span><small>{cover.category}</small><strong>{cover.name}</strong></span>{form.cover_images.includes(cover.id) && <Check size={17} weight="bold" className="pb-cover-check" />}</button>)}</div><button type="button" className="text-xs text-primary mt-3" onClick={() => setForm(prev => ({...prev, cover_images: []}))}>Use theme gradient instead</button><p className="pb-help">Selecting a cover replaces the current carousel. Add your own images below to create a mixed carousel.</p></div></>}
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-1.5">Cover images <span className="text-text-muted normal-case tracking-normal">(shown behind the apply page carousel)</span></label>
            <div className="flex flex-wrap gap-2" data-testid="cover-images-list">
              {form.cover_images.map((img, i) => (
                <div key={img + i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-border group" data-testid={`cover-image-thumb-${i}`}>
                  <img src={fileUrl(img)} className="w-full h-full object-cover" alt="" />
                  <button type="button" onClick={() => removeCoverImage(i)} data-testid={`cover-image-remove-${i}`}
                    className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <X size={11} weight="bold" />
                  </button>
                </div>
              ))}
              <label className="w-20 h-20 rounded-lg border-2 border-dashed border-border hover:border-primary flex items-center justify-center cursor-pointer text-text-muted hover:text-primary transition-colors"
                data-testid="cover-image-add-btn">
                {uploadingCover ? <span className="text-[10px]">Uploading…</span> : <Plus size={20} weight="bold" />}
                <input type="file" accept="image/*" className="hidden" disabled={uploadingCover}
                  onChange={(e) => { uploadCoverImage(e.target.files?.[0]); e.target.value = ''; }} />
              </label>
            </div>
            <div className="text-[11px] text-text-muted mt-1.5">Optional. Rotates behind the program name on the public apply page. Leave empty to use a default background.</div>
          </div>
          </div>

</>);
  const publishControls = (<>
          <div >
            {isIncubator && <div className="pb-section-title"><h4>Ready for your next cohort?</h4><p>Choose when applications open. Save to apply your changes.</p><div className="pb-checklist"><span>{form.name.trim() ? '✓' : '○'} Program name</span><span>{form.questions.some(q => q.type !== 'section' && stripHtml(q.label)) ? '✓' : '○'} Application questions</span><span>{form.cover_images.length ? '✓' : '○'} Cover image (optional)</span></div></div>}
            <label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-2">Visibility & Status</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { v: 'draft',     label: 'Draft',     hint: 'Hidden, work-in-progress', cls: 'bg-text-muted/10 border-text-muted text-text-secondary' },
                { v: 'scheduled', label: 'Scheduled', hint: 'Auto-opens on a date',     cls: 'bg-info/10 border-info text-info' },
                { v: 'open',      label: 'Open',      hint: 'Live & accepting',         cls: 'bg-success/10 border-success text-success' },
                { v: 'closed',    label: 'Closed',    hint: 'Not accepting new applicants', cls: 'bg-warning/10 border-warning text-warning' },
              ].map(s => (
                <button key={s.v} type="button" onClick={() => setForm({ ...form, status: s.v })}
                  className={`px-3 py-2 rounded-lg border text-left transition-all ${form.status === s.v ? s.cls : 'border-border text-text-muted hover:border-text-muted'}`}
                  data-testid={`status-${s.v}-btn`}>
                  <div className="text-xs font-bold">{s.label}</div>
                  <div className="text-[10px] opacity-70">{s.hint}</div>
                </button>
              ))}
            </div>
            {form.status === 'scheduled' && (
              <div className="mt-3" data-testid="schedule-open-section">
                <label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-1.5">Open at</label>
                <input
                  type="datetime-local"
                  value={form.scheduled_open_at}
                  onChange={e => setForm({ ...form, scheduled_open_at: e.target.value })}
                  min={nowDatetimeLocalValue()}
                  className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary"
                  data-testid="scheduled-open-at-input"
                />
                <div className="text-[11px] text-text-muted mt-1.5">The program will be hidden from the public apply page until this time. Times are local; we'll auto-flip the status when it hits.</div>
              </div>
            )}
            {['open', 'scheduled'].includes(form.status) && (
              <div className="mt-3" data-testid="open-until-section">
                <label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-1.5">Open until <span className="text-text-muted normal-case tracking-normal">(optional deadline)</span></label>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="datetime-local"
                    value={form.open_until}
                    onChange={e => setForm({ ...form, open_until: e.target.value })}
                    min={nowDatetimeLocalValue()}
                    className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary"
                    data-testid="open-until-input"
                  />
                  {form.open_until && (
                    <button type="button" onClick={() => setForm({ ...form, open_until: '' })}
                      className="text-xs text-text-muted hover:text-danger px-2 py-1 flex-shrink-0" data-testid="open-until-clear">Clear</button>
                  )}
                </div>
                <div className="text-[11px] text-text-muted mt-1.5">Applications auto-close at this time. Leave blank to stay open until you close it manually. Times are local.</div>
              </div>
            )}
          </div>
</>);

  const modal = (
    <div className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto ${isIncubator ? 'pb-overlay' : ''}`} data-testid="program-form-modal">
      <div ref={workspaceRef} role="dialog" aria-modal="true" aria-label={isIncubator ? 'Program studio' : 'Watchlist builder'} className={`bg-surface border border-border rounded-xl w-[760px] max-w-full my-8 flex flex-col max-h-[90vh] relative ${isIncubator ? 'pb-workspace' : ''}`}>
        {/* Google-Forms-style floating add toolbar — docked to the right, outside the box, on large screens */}
        <div className={`${isIncubator ? '!hidden' : ''} hidden lg:flex absolute top-1/2 -translate-y-1/2 right-0 translate-x-[calc(100%+0.75rem)] z-10`}>
          <div className="flex flex-col items-center gap-1 bg-surface border border-border rounded-full shadow-lg py-2 px-1.5">
            <button type="button" onClick={addQ} data-testid="add-question-btn" title="Add question"
              className="group w-10 h-10 rounded-full flex items-center justify-center text-text-secondary hover:text-primary hover:bg-primary/10 transition-all">
              <Plus size={20} weight="bold" className="transition-transform duration-200 group-hover:rotate-90 group-hover:scale-110" />
            </button>
            <span className="w-5 h-px bg-border" />
            <button type="button" onClick={addFile} data-testid="add-file-btn" title="Add file upload"
              className="w-10 h-10 rounded-full flex items-center justify-center text-text-secondary hover:text-primary hover:bg-primary/10 transition-all">
              <Paperclip size={18} weight="bold" />
            </button>
            <button type="button" onClick={addImage} data-testid="add-image-btn" title="Add image upload"
              className="w-10 h-10 rounded-full flex items-center justify-center text-text-secondary hover:text-primary hover:bg-primary/10 transition-all">
              <Image size={18} weight="bold" />
            </button>
            <span className="w-5 h-px bg-border" />
            <button type="button" onClick={addSection} data-testid="add-section-btn" title="Add section"
              className="w-10 h-10 rounded-full flex items-center justify-center text-text-secondary hover:text-primary hover:bg-primary/10 transition-all">
              <Rows size={18} weight="bold" />
            </button>
          </div>
        </div>
        <div className={`flex items-center justify-between px-6 pt-6 pb-4 shrink-0 ${isIncubator ? 'pb-studio-header' : ''}`}>
          <div className={isIncubator ? 'pb-studio-identity' : ''}>{isIncubator&&<button type="button" className="pb-studio-back" onClick={closeBuilder} aria-label="Back to programs" data-testid="builder-back"><ArrowLeft size={17}/>Back</button>}{isIncubator && <span className="pb-studio-label">Program studio</span>}<h3 className="font-heading font-bold text-xl text-text-primary">{isIncubator ? (form.name || 'Untitled program') : `${initial ? 'Edit' : 'Create'} Watchlist`}</h3></div>
          <div className="flex items-center gap-3">
            <button hidden={isIncubator} type="button" onClick={() => setShowPreview(true)} data-testid="preview-program-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium text-text-secondary hover:text-primary border border-border rounded-lg hover:border-primary/40">
              <Eye size={14} weight="bold" /> Full preview
            </button>
            <button type="button" onClick={closeBuilder} aria-label="Close builder" className="text-text-muted hover:text-text-primary"><X size={20} /></button>
          </div>
        </div>
        {isIncubator ? <>
          <div className="pb-canvas-toolbar"><span><i className="pb-canvas-status-dot"/>Editing your program<span className="pb-canvas-status-label">{form.status === 'draft' ? 'Draft' : form.status === 'scheduled' ? 'Scheduled' : form.status === 'closed' ? 'Closed' : 'Open'}</span></span><div><button type="button" onClick={() => setBuilderTab('design')}><Image size={15}/>Design</button><button type="button" onClick={() => setBuilderTab('publish')}><Gear size={15}/> Publishing</button><button type="button" onClick={() => setShowPreview(true)}><Eye size={15}/> Preview</button></div></div>
          <div className="pb-canvas-mobile-nav"><button type="button" aria-pressed={canvasPage === 'cover'} onClick={() => setCanvasPage('cover')}>Program cover</button><button type="button" aria-pressed={canvasPage === 'form'} onClick={() => setCanvasPage('form')}>Application</button></div>
          <div className={`pb-direct-canvas pb-canvas-${canvasPage}`} inert={builderTab !== 'canvas' || showDiscard || blockPicker || showPreview ? true : undefined}>
            <ProgramPublicFrame program={{...form, open_until: form.open_until ? datetimeLocalToUTC(form.open_until) : null, fund_name: fund?.fund_name || 'Your incubator', fund_logo_url: fund?.logo_url}} isIncubator coverEditor={<ProgramCanvasCover form={form} setForm={setForm} onDesign={() => setBuilderTab('design')}/>}>{questionCanvas}</ProgramPublicFrame>
          </div>
          {visiblePanel && <div className="pb-settings-backdrop" data-state={builderTab === visiblePanel ? 'open' : 'closing'} aria-hidden={builderTab !== visiblePanel || undefined} inert={builderTab !== visiblePanel ? true : undefined} onMouseDown={e => { if (e.target === e.currentTarget) setBuilderTab('canvas'); }}><section role="dialog" aria-modal="true" aria-label={visiblePanel === 'design' ? 'Cover and theme' : visiblePanel === 'add' ? 'Add content' : 'Publishing settings'} className="pb-canvas-settings pb-editor"><header><strong>{visiblePanel === 'design' ? 'Cover & theme' : visiblePanel === 'add' ? 'Add content' : 'Publishing settings'}</strong><button type="button" autoFocus onClick={() => setBuilderTab('canvas')} aria-label="Back to canvas"><X size={18}/></button></header><div className="overflow-y-auto" ref={editorRef}>{visiblePanel === 'design' ? designControls : visiblePanel === 'add' ? contentControls : <>{publishControls}            <details className="pb-review-settings"><summary><span><Sparkle size={16} weight="duotone"/>Review preferences</span><small>Optional · Private</small></summary><div><label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-1.5" htmlFor="program-studio-criteria">AI evaluation instructions</label><textarea id="program-studio-criteria" rows={3} value={form.ai_eval_criteria} onChange={e=>setForm({...form,ai_eval_criteria:e.target.value})} placeholder="e.g. Prioritize technical founders with a working climate hardware prototype." className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary resize-y" data-testid="program-ai-criteria-input"/><p className="pb-overview-hint">Help your team evaluate fit. These instructions are never shown on the public application page.</p></div></details></>}</div><button type="button" className="pb-settings-done" onClick={() => setBuilderTab('canvas')}>Done · Back to canvas</button></section></div>}
        </> : <div className="contents"><div ref={editorRef} className="space-y-4 flex-1 overflow-y-auto px-6 py-2">          <div hidden={isIncubator && builderTab !== 'details'} className="pb-overview space-y-5">
            {isIncubator && <div className="pb-overview-intro"><span className="pb-overview-symbol"><Buildings size={23} weight="duotone"/></span><span className="pb-eyebrow">THE FIRST IMPRESSION</span><h4>Start with your story.</h4><p>A clear promise. The right founders. A program worth applying to.</p></div>}
            <div className="pb-overview-group">
              {isIncubator && <div className="pb-overview-group-heading"><span>Program identity</span><small>Shown to applicants</small></div>}
              <div className="pb-overview-field"><label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-1.5" htmlFor="program-studio-name">Program name <span className="text-danger">*</span></label><input id="program-studio-name" type="text" value={form.name} onChange={e => setForm({...form,name:e.target.value})} placeholder={isIncubator ? 'e.g. Frontier Founders 2026' : 'e.g. Q1 Dealflow Watchlist'} className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary" data-testid="program-name-input"/></div>
              <div className="pb-overview-field"><label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-1.5" htmlFor="program-studio-headline">Cover subheading</label><textarea id="program-studio-headline" rows={2} value={form.hero_text} onChange={e => setForm({...form,hero_text:e.target.value})} placeholder="Big ideas deserve a bigger beginning." className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary" data-testid="program-hero-input"/><p className="pb-overview-hint">A short supporting line below the program name.</p></div>
            </div>
            <div className="pb-overview-group"><div className="pb-overview-group-heading"><span>The invitation</span><small>Why should founders apply?</small></div><label className="sr-only">Program description</label><RichTextEditor className="pb-overview-description-editor" value={form.description} onChange={html=>setForm({...form,description:html})} placeholder="Who is this for? What will founders build, learn, or unlock? Tell them what makes your program different." data-testid="program-desc-input"/><p className="pb-overview-hint">A few focused lines work best. Your formatting appears on the live cover.</p></div>
            {isIncubator && <div className="pb-cover-section-builder"><div className="pb-overview-group-heading"><span>More about the program</span><small>Optional cover sections</small></div><p className="pb-overview-hint">Add the details founders need before they apply. Only sections with content and visibility switched on appear on your cover.</p><div className="pb-cover-section-picker">{PROGRAM_COVER_SECTIONS.map(preset => {const added=form.cover_sections.some(section=>section.kind===preset.kind);return <button type="button" key={preset.kind} disabled={added} data-testid={`add-cover-section-${preset.kind}`} onClick={()=>setForm(prev=>({...prev,cover_sections:[...prev.cover_sections,{kind:preset.kind,title:preset.title,body:'',enabled:true}]}))}>{added ? <Check size={13}/> : <Plus size={13}/>}<span>{preset.title}</span></button>;})}</div><div className="pb-cover-section-editors">{form.cover_sections.map((section,index)=>{const preset=PROGRAM_COVER_SECTIONS.find(item=>item.kind===section.kind);const update=patch=>setForm(prev=>({...prev,cover_sections:prev.cover_sections.map((item,i)=>i===index?{...item,...patch}:item)}));return <div className="pb-cover-section-editor" key={section.kind} data-testid={`cover-section-editor-${section.kind}`}><div className="pb-cover-section-actions"><span>{preset?.title || 'Program details'}</span><button type="button" aria-pressed={section.enabled !== false} onClick={()=>update({enabled:section.enabled === false})}>{section.enabled === false ? 'Hidden' : 'Shown'}</button><button type="button" disabled={index===0} aria-label={`Move ${section.title || preset?.title} up`} onClick={()=>setForm(prev=>({...prev,cover_sections:arrayMove(prev.cover_sections,index,index-1)}))}><CaretUp size={13}/></button><button type="button" disabled={index===form.cover_sections.length-1} aria-label={`Move ${section.title || preset?.title} down`} onClick={()=>setForm(prev=>({...prev,cover_sections:arrayMove(prev.cover_sections,index,index+1)}))}><CaretDown size={13}/></button><button type="button" aria-label={`Remove ${section.title || preset?.title}`} onClick={()=>setForm(prev=>({...prev,cover_sections:prev.cover_sections.filter((_,i)=>i!==index)}))}><Trash size={13}/></button></div><input type="text" maxLength={80} aria-label={`${preset?.title} section heading`} value={section.title} onChange={event=>update({title:event.target.value})}/><textarea rows={3} maxLength={2000} aria-label={`${preset?.title} section content`} placeholder={preset?.hint} value={section.body} onChange={event=>update({body:event.target.value})}/></div>;})}</div></div>}
            <details className="pb-review-settings"><summary><span><Sparkle size={16} weight="duotone"/>Review preferences</span><small>Optional · Private</small></summary><div><label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-1.5" htmlFor="program-studio-criteria">AI evaluation instructions</label><textarea id="program-studio-criteria" rows={3} value={form.ai_eval_criteria} onChange={e=>setForm({...form,ai_eval_criteria:e.target.value})} placeholder="e.g. Prioritize technical founders with a working climate hardware prototype." className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary resize-y" data-testid="program-ai-criteria-input"/><p className="pb-overview-hint">Help your team evaluate fit. These instructions are never shown on the public application page.</p></div></details>
          </div>
{designControls}{questionCanvas}{publishControls}</div></div>}

        <div className={`flex flex-wrap justify-end gap-2 px-6 py-4 shrink-0 border-t border-border ${isIncubator ? 'pb-studio-footer' : ''}`}>
          {isIncubator && <span className="pb-save-state">{hasChanges ? 'Unsaved changes' : 'No unsaved changes'}</span>}
          <button onClick={closeBuilder} className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary">Cancel</button>
          {!initial && (
            <button onClick={() => handleSave('draft')} disabled={saving}
              className="px-4 py-2 bg-background border border-border rounded-lg text-sm font-medium text-text-primary hover:border-primary disabled:opacity-50"
              data-testid="save-as-draft-btn">
              Save as draft
            </button>
          )}
          <button onClick={() => handleSave()} disabled={saving} className="px-5 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50" data-testid="save-program-btn">
            {saving ? 'Saving...' : initial ? 'Save changes' : (form.status === 'draft' ? 'Save draft' : form.status === 'scheduled' ? 'Schedule program' : 'Publish program')}
          </button>
        </div>
      </div>

      {showDiscard && <div className="pb-discard-backdrop"><div role="alertdialog" aria-modal="true" aria-labelledby="pb-discard-title" className="pb-discard-dialog"><h4 id="pb-discard-title">Discard unsaved changes?</h4><p>Your program edits have not been saved.</p><div><button type="button" autoFocus onClick={() => setShowDiscard(false)}>Keep editing</button><button type="button" onClick={onClose} data-testid="discard-program-changes">Discard & close</button></div></div></div>}

      {/* Question-block chooser — pick which fields of the block to add */}
      {blockPicker && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
          data-testid="block-picker-modal"
          onMouseDown={e => { if (e.target === e.currentTarget) setBlockPicker(null); }}>
          <div className="bg-surface border border-border rounded-xl w-full max-w-sm p-5 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-heading font-bold text-base text-text-primary">{blockPicker.label}</h4>
              <button type="button" onClick={() => setBlockPicker(null)} className="text-text-muted hover:text-text-primary"><X size={16} /></button>
            </div>
            <p className="text-[12px] text-text-muted mb-4">Select which questions to add under this block.</p>
            <div className="space-y-1.5 overflow-y-auto">
              {blockPicker.fields.map(f => (
                <label key={f.key}
                  className="flex items-start gap-2.5 px-3 py-2 rounded-lg border border-border bg-background cursor-pointer hover:border-primary/40">
                  <input type="checkbox" checked={!!blockSel[f.key]}
                    onChange={e => setBlockSel({ ...blockSel, [f.key]: e.target.checked })}
                    className="mt-0.5" data-testid={`block-field-${f.key}`} />
                  <span className="flex-1">
                    <span className="block text-[13px] text-text-primary">{f.label}</span>
                    <span className="block text-[10px] uppercase tracking-wider text-text-muted">{QUESTION_TYPES.find(t => t.value === f.type)?.label || f.type}</span>
                  </span>
                </label>
              ))}
            </div>
            <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-border">
              <div className="flex gap-3">
                <button type="button" onClick={() => setBlockSel(Object.fromEntries(blockPicker.fields.map(f => [f.key, true])))} className="text-[11px] text-primary hover:text-primary/80">Select all</button>
                <button type="button" onClick={() => setBlockSel({})} className="text-[11px] text-text-muted hover:text-text-secondary">Clear</button>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setBlockPicker(null)} className="px-3 py-1.5 text-[12px] text-text-secondary hover:text-text-primary">Cancel</button>
                <button type="button" onClick={addBlockSelected} disabled={selectedCount === 0}
                  data-testid="add-block-selected-btn"
                  className="px-3 py-1.5 text-[12px] font-medium bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50">
                  Add{selectedCount ? ` ${selectedCount}` : ''} question{selectedCount === 1 ? '' : 's'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPreview && <ProgramPreviewModal form={form} isIncubator={isIncubator} onClose={() => setShowPreview(false)} />}
    </div>
  );
  return isIncubator ? createPortal(modal, document.body) : modal;
};

// AI fit-score pill — reused on the kanban card, the table row and the detail modal.
const FitBadge = ({ score, label, size = 'xs' }) => {
  if (score == null) return null;
  const tone = score >= 75 ? 'bg-success/10 text-success' : score >= 50 ? 'bg-warning/10 text-warning' : 'bg-danger/10 text-danger';
  const sizeCls = size === 'sm' ? 'text-[11px] px-2 py-1 gap-1' : 'text-[9px] px-1.5 py-0.5 gap-0.5';
  return (
    <span className={`inline-flex items-center rounded-full font-semibold uppercase tracking-wide flex-shrink-0 ${tone} ${sizeCls}`}
      title={label ? `AI: ${label}` : 'AI fit score'} data-testid="ai-fit-badge">
      <Sparkle size={size === 'sm' ? 11 : 8} weight="fill" /> {score}
    </span>
  );
};

// The incubator card presents the submitted company and saved evaluation evidence.
const ApplicantCardProfile = ({app,questions}) => {
  const {formatCompact}=useCurrency();
  const [logoFailed,setLogoFailed]=useState(false);
  const company=app.startup||{};
  const name=company.startup_name||app.imported_startup_name||app.applicant_name||app.applicant_email||'Applicant';
  const founder=app.applicant_name||company.founder_name;
  const initials=name.trim().split(/\s+/).slice(0,2).map(word=>word[0]).join('').toUpperCase();
  const manual=getCumulativeScore(app.evaluation_scores,questions||[]);
  const ai=app.ai_fit_score!=null&&app.ai_fit_score!==''&&Number.isFinite(Number(app.ai_fit_score))?Number(app.ai_fit_score):null;
  const metrics=[
    ['MRR',company.latest_metrics?.mrr,value=>formatCompact(value)],
    ['Runway',company.latest_metrics?.runway_months,value=>Number(value)===999?'Profitable':`${Math.round(Number(value)*10)/10} mo`],
    ['Team',company.team_size,value=>String(value)],
  ].filter(([,value])=>value!=null&&value!==''&&Number.isFinite(Number(value)));
  const scored=manual?.avg!=null;
  return <div className="ie-profile">
    <div className="ie-profile-identity">
      {company.logo_url&&!logoFailed?<img src={fileUrl(company.logo_url)} alt="" onError={()=>setLogoFailed(true)}/>:<span className="ie-profile-monogram">{initials}</span>}
      <div><strong>{name}</strong>{(company.sector||company.funding_stage)&&<p>{[company.sector,company.funding_stage].filter(Boolean).join(' · ')}</p>}</div>
    </div>
    <div className="ie-profile-founder" title={[founder,app.applicant_email].filter(Boolean).join(' · ')}>{founder&&founder!==name?<span>{founder}</span>:<span>{app.applicant_email}</span>}{app.submitted_at&&<time dateTime={app.submitted_at} title={formatDateTime(app.submitted_at)}>{timeAgo(app.submitted_at)}</time>}</div>
    {metrics.length>0&&<div className="ie-profile-metrics">{metrics.slice(0,2).map(([label,value,format])=><span key={label}>{label} <b>{format(value)}</b></span>)}</div>}
    <div className={`ie-profile-scores ${manual?'':'is-single'}`}>
      <div className="ie-profile-score" data-testid={`app-ai-score-${app.id}`} title={ai==null?'Not evaluated':app.ai_fit_label||'AI evaluation'} aria-label={ai==null?'AI fit: not evaluated':`AI fit: ${ai} out of 100`}><span><Sparkle size={12}/>AI fit</span><strong>{ai==null?'—':ai}<small>{ai==null?'':'/100'}</small></strong></div>
      {manual&&<div className="ie-profile-score is-team" data-testid={`app-team-score-${app.id}`} title={scored?`${manual.scoredCount}/${manual.totalCount} questions scored · ${manual.judgeCount} judge${manual.judgeCount===1?'':'s'}`:'Not scored'} aria-label={scored?`Team score: ${manual.avg} out of 10`:'Team score: not scored'}><span><Users size={12}/>Team</span><strong>{scored?manual.avg:'—'}<small>{scored?'/10':''}</small></strong></div>}
    </div>
    <div className="ie-profile-foot"><div>{app.comments_count>0&&<span title={`${app.comments_count} comments`}><ChatCircleDots size={12}/>{app.comments_count}</span>}{app.decision_notification?.sent&&app.decision_notification.category===app.status&&<span title="Current decision emailed" aria-label="Current decision emailed"><EnvelopeSimple size={12}/><Check size={10}/></span>}<StartupBadges badges={company.badges||[]} iconOnly size="xs"/></div><span className="ie-profile-open">Review<ArrowRight size={12}/></span></div>
    {app.historical_import&&<div className="ie-profile-import"><ImportedProfileBadge app={app}/></div>}
  </div>;
};

// Applicant cards open the company record and remain draggable between stages.
const AppCard = ({ app, onClick, locked, evalQuestions, compact = false }) => {
  const { formatCompact } = useCurrency();
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: app.id,
    data: { app },
    disabled: locked,
  });

  const handleClickCard = onClick;

  return (
    <div ref={setNodeRef}
      onClick={handleClickCard}
      {...(locked ? {} : attributes)}
      {...(locked ? {} : listeners)}
      onKeyDown={event=>{if(event.target===event.currentTarget&&event.key==='Enter'){event.preventDefault();handleClickCard(event);}else if(!locked)listeners?.onKeyDown?.(event);}}
      className={`program-application-card group relative w-full text-left bg-surface border rounded-lg p-2.5 transition-all ${locked ? 'cursor-default' : 'cursor-pointer'} border-border hover:border-primary/40 ${isDragging ? 'opacity-30' : ''}`}
      data-testid={`app-card-${app.id}`}
      data-incubator={compact?"true":undefined}
      data-locked={locked ? 'true' : 'false'}
    >
      {compact ? <ApplicantCardProfile app={app} questions={evalQuestions}/> : <>      <div className="flex items-start gap-2">
        {app.startup?.logo_url ? <img src={fileUrl(app.startup.logo_url)} className="w-6 h-6 rounded-full object-cover flex-shrink-0" alt="" /> :
          <div className={`w-6 h-6 ${app.startup ? 'bg-success/10' : 'bg-primary/10'} rounded-full flex items-center justify-center flex-shrink-0`}>
            <span className={`${app.startup ? 'text-success' : 'text-primary'} font-mono text-[9px] font-bold`}>{(app.imported_startup_name || app.applicant_name || app.applicant_email).substring(0, 2).toUpperCase()}</span>
          </div>}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-1.5">
            <div className="text-xs font-medium text-text-primary truncate">{app.startup?.startup_name || app.imported_startup_name || app.applicant_name || app.applicant_email.split('@')[0]}</div>
            {/* (Iter 55) Stub startups from bulk upload have startup_id set but
                is_stub=true — they are effectively "Quick" until the founder claims
                the account. Only genuinely claimed startups earn the Full badge. */}
            {app.historical_import ? <ImportedProfileBadge app={app} /> : app.startup && !app.startup.is_stub ? (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-success/10 text-success text-[9px] font-semibold uppercase tracking-wide flex-shrink-0" data-testid={`profile-badge-full-${app.id}`} title="Full Founderboard profile linked — metrics, founder info & monthly updates available">
                <Sparkle size={8} weight="fill" /> Full
              </span>
            ) : (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-text-muted/10 text-text-muted text-[9px] font-semibold uppercase tracking-wide flex-shrink-0" data-testid={`profile-badge-thin-${app.id}`} title={app.startup?.is_stub ? 'Bulk-imported stub — founder hasn’t claimed their Founderboard account yet' : 'Quick Apply — only the email and answers below are available'}>
                Quick
              </span>
            )}
          </div>
          <div className="flex items-center justify-between gap-1.5 mt-0.5">
            <div className="min-w-0 flex items-center gap-1 text-[10px] text-text-muted">
              {app.startup?.sector && <span className="truncate">{app.startup.sector}</span>}
              {app.startup?.sector && <span className="flex-shrink-0">·</span>}
              <span className="truncate">{app.applicant_email}</span>
            </div>
            {app.comments_count > 0 && (
              <span className="inline-flex items-center gap-0.5 text-[10px] text-text-muted flex-shrink-0" title={`${app.comments_count} comment${app.comments_count === 1 ? '' : 's'}`} data-testid={`card-comments-${app.id}`}>
                <ChatCircleDots size={10} weight="bold" /> {app.comments_count}
              </span>
            )}
          </div>
        </div>
      </div>

      {app.decision_notification?.sent && app.decision_notification.category === app.status && (
        <div className={`mt-2 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${app.status === 'accepted' ? 'bg-success/10 text-success' : app.status === 'rejected' ? 'bg-danger/10 text-danger' : 'bg-warning/10 text-warning'}`}
          data-testid={`app-notified-${app.id}`}
          title={app.decision_notification.sent_at ? `Email sent ${new Date(app.decision_notification.sent_at).toLocaleString()}` : 'Current outcome emailed'}>
          <EnvelopeSimple size={12} weight="bold" /><Check size={10} weight="bold" />
          {app.status === 'accepted' ? 'Acceptance sent' : app.status === 'rejected' ? 'Rejection sent' : 'Waitlist sent'}
        </div>
      )}

      {/* Compact at-a-glance metrics row — only when FULL profile has data */}
      {app.startup && (app.startup.latest_metrics || app.startup.team_size) && (
        <div className="grid grid-cols-3 gap-1 mt-1.5" data-testid={`app-glance-${app.id}`}>
          <div className="min-w-0">
            <div className="text-[8px] uppercase tracking-wider text-text-muted leading-none">MRR</div>
            <div className="font-mono text-[10px] font-semibold text-text-primary truncate" data-testid={`app-glance-mrr-${app.id}`}>
              {app.startup.latest_metrics?.mrr ? formatCompact(app.startup.latest_metrics.mrr) : '—'}
            </div>
          </div>
          <div className="min-w-0">
            <div className="text-[8px] uppercase tracking-wider text-text-muted leading-none">Runway</div>
            <div className={`font-mono text-[10px] font-semibold truncate ${
              app.startup.latest_metrics?.runway_months == null ? 'text-text-muted'
                : app.startup.latest_metrics.runway_months < 3 ? 'text-danger'
                : app.startup.latest_metrics.runway_months < 6 ? 'text-warning' : 'text-text-primary'
            }`} data-testid={`app-glance-runway-${app.id}`}>
              {app.startup.latest_metrics?.runway_months != null
                ? `${Math.round(app.startup.latest_metrics.runway_months)}mo`
                : '—'}
            </div>
          </div>
          <div className="min-w-0">
            <div className="text-[8px] uppercase tracking-wider text-text-muted leading-none">Team</div>
            <div className="font-mono text-[10px] font-semibold text-text-primary truncate" data-testid={`app-glance-team-${app.id}`}>
              {app.startup.latest_metrics?.headcount != null
                ? String(app.startup.latest_metrics.headcount)
                : (app.startup.team_size || '—')}
            </div>
          </div>
        </div>
      )}

      {(() => {
        const evalSummary = getCumulativeScore(app.evaluation_scores, evalQuestions || []);
        if (app.ai_fit_score == null && !evalSummary && !app.startup?.badges?.length) return null;
        return (
          <div className="flex items-center justify-between gap-1.5 mt-1.5" data-testid={`app-card-footer-${app.id}`}>
            <div className="flex items-center gap-1.5 min-w-0">
              <FitBadge score={app.ai_fit_score} label={app.ai_fit_label} />
              <CumulativeScoreBadge summary={evalSummary} />
            </div>
            <StartupBadges badges={app.startup?.badges || []} iconOnly size="xs" className="program-card-badges ml-auto shrink-0" />
          </div>
        );
      })()}</>}

    </div>
  );
};

const BoardLaneHeading = ({stage,count,stageTotal,filtering,incubator,onNotify,pendingNotifications}) => {
  const LaneIcon={received:Tray,accepted:CheckCircle,waitlisted:Clock,rejected:XCircle}[stage.key];
  const canNotify=CLOSE_EMAIL_CATEGORIES.includes(stage.key);
  return <div className={`sticky top-0 z-10 flex items-center justify-between px-4 pt-3.5 pb-2.5 bg-surface bg-gradient-to-b ${stage.headerGradient} ${incubator?'ie-lane-heading':''}`}>
    <div className="flex items-center gap-2">
      {incubator?<span className="ie-lane-symbol"><LaneIcon size={16} weight="bold" aria-hidden="true"/></span>:<span className={`w-2 h-2 rounded-full ${stage.dot}`} />}
      <span className="text-[13px] font-semibold text-text-primary">{stage.kanbanLabel}</span>
    </div>
    <div className="ie-lane-actions">
      {canNotify && <button type="button" className="ie-lane-notify" onClick={() => onNotify(stage.key)} disabled={pendingNotifications===0}
        aria-label={`Notify ${stage.kanbanLabel.toLowerCase()} applicants`}
        title={pendingNotifications?`Review notifications for ${pendingNotifications} pending applicants, including those hidden by filters.`:'No pending decision notifications.'}
        data-testid={`notify-column-${stage.key}`}><EnvelopeSimple size={16}/></button>}
      <span className={`text-[13px] font-semibold text-text-secondary tabular-nums ${incubator?'ie-lane-count':''}`}
        title={filtering?`${count} of ${stageTotal} match the active filters`:undefined}>
        {count}{filtering&&count!==stageTotal&&<span className="text-text-muted font-normal">/{stageTotal}</span>}
      </span>
    </div>
  </div>;
};

// Drop-target wrapper for kanban column
const DroppableColumn = ({ stageKey, children, className, ...rest }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `col-${stageKey}` });
  return (
    <div ref={setNodeRef} data-drop-active={isOver?"true":"false"} className={`${className} ${isOver ? 'ring-2 ring-primary/50' : ''}`} {...rest}>
      {children}
    </div>
  );
};

// Flat table view of every applicant — the GitHub-issues-list counterpart to the
// kanban. Status is editable inline; clicking a row opens the company record.
const ApplicationsTable = ({ grouped, onRowClick, onStatusChange, evalQuestions, locked, sortRows, onNotify, notificationGrouped = grouped, compact = false }) => {
  const { formatCompact } = useCurrency();

  const stageRows = STAGES.flatMap(s => (grouped[s.key] || []).map(a => ({ ...a, _stage: s })));
  const rows = sortRows ? [...stageRows].sort(sortRows) : stageRows;

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-x-auto" data-testid="applications-table-view">
      {!compact && <div className="flex flex-wrap items-center gap-2 mb-3" role="group" aria-label="Notify applicants by decision">
        {STAGES.filter(stage => CLOSE_EMAIL_CATEGORIES.includes(stage.key)).map(stage => {
          const pending = (notificationGrouped[stage.key] || []).filter(a => a.applicant_email?.trim() && !(a.decision_notification?.sent && a.decision_notification.category === a.status)).length;
          return <button key={stage.key} type="button" onClick={() => onNotify(stage.key)} disabled={!pending}
            className="program-notify-button inline-flex items-center gap-1.5 rounded-lg border px-3 h-9 text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`table-notify-${stage.key}`}
            title={`Notify pending ${stage.label.toLowerCase()} applicants, including those hidden by filters.`}>
            <EnvelopeSimple size={14} /> Notify {stage.kanbanLabel}{pending ? ` (${pending})` : ''}
          </button>;
        })}
      </div>
      }
      <div className="flex-1 overflow-auto border border-border rounded-lg bg-surface">
        <table className="w-full text-left border-collapse">
          <thead className="sticky top-0 z-10 bg-surface">
            <tr className="border-b border-border">
              {['Startup', 'Profile', 'Sector', 'MRR', 'Runway', 'Fit', ...(evalQuestions.length ? ['Eval'] : []), 'Applied', '', 'Status', 'Notification'].map((h, i) => (
                <th key={i} className="px-3 py-2 text-[10px] uppercase tracking-wider text-text-muted font-semibold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={10 + (evalQuestions.length ? 1 : 0)} className="px-3 py-10 text-center text-sm text-text-muted italic" data-testid="table-empty">No applications in this view</td></tr>
            )}
            {rows.map(app => {
              const s = app.startup;
              return (
                <tr key={app.id}
                  onClick={event => onRowClick(app.id,event.currentTarget)}
                  className="border-b border-border last:border-0 cursor-pointer transition-colors hover:bg-background"
                  data-testid={`app-row-${app.id}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {s?.logo_url ? <img src={fileUrl(s.logo_url)} className="w-6 h-6 rounded object-cover flex-shrink-0" alt="" /> :
                        <div className={`w-6 h-6 ${s ? 'bg-success/10' : 'bg-text-muted/10'} rounded flex items-center justify-center flex-shrink-0`}>
                          <span className={`${s ? 'text-success' : 'text-text-muted'} font-mono text-[10px] font-bold`}>{(app.imported_startup_name || app.applicant_name || app.applicant_email).substring(0, 2).toUpperCase()}</span>
                        </div>}
                      <div className="min-w-0">
                        <div className="text-[13px] font-medium text-text-primary truncate">{s?.startup_name || app.imported_startup_name || app.applicant_name || app.applicant_email.split('@')[0]}</div>
                        <div className="text-[10px] text-text-muted truncate">{app.applicant_email}</div>
                        {s && <StartupBadges badges={s.badges} size="xs" className="mt-1" />}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {app.historical_import ? <ImportedProfileBadge app={app} /> : s && !s.is_stub ? (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-success/10 text-success text-[9px] font-semibold uppercase tracking-wide"><Sparkle size={8} weight="fill" /> Full</span>
                    ) : (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-text-muted/10 text-text-muted text-[9px] font-semibold uppercase tracking-wide">Quick</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[12px] text-text-secondary whitespace-nowrap">{s?.sector || '—'}</td>
                  <td className="px-3 py-2 font-mono text-[12px] text-text-primary whitespace-nowrap">
                    {s?.latest_metrics?.mrr ? formatCompact(s.latest_metrics.mrr) : '—'}
                  </td>
                  <td className={`px-3 py-2 font-mono text-[12px] whitespace-nowrap ${
                    s?.latest_metrics?.runway_months == null ? 'text-text-muted'
                      : s.latest_metrics.runway_months < 3 ? 'text-danger'
                      : s.latest_metrics.runway_months < 6 ? 'text-warning' : 'text-text-primary'
                  }`}>
                    {s?.latest_metrics?.runway_months != null ? `${Math.round(s.latest_metrics.runway_months)}mo` : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <FitBadge score={app.ai_fit_score} label={app.ai_fit_label} />
                  </td>
                  {evalQuestions.length > 0 && (
                    <td className="px-3 py-2" title="Open the applicant to score individual questions">
                      <CumulativeScoreBadge summary={getCumulativeScore(app.evaluation_scores, evalQuestions)} />
                    </td>
                  )}
                  <td className="px-3 py-2 text-[11px] text-text-muted whitespace-nowrap" title={formatDateTime(app.submitted_at)}>{timeAgo(app.submitted_at)}</td>
                  <td className="px-3 py-2">
                    {app.comments_count > 0 && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-text-muted" title={`${app.comments_count} comment${app.comments_count === 1 ? '' : 's'}`} data-testid={`row-comments-${app.id}`}>
                        <ChatCircleDots size={12} weight="bold" /> {app.comments_count}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                    {locked ? (
                      <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wide ${app._stage.color} ${app._stage.text}`}>{app._stage.label}</span>
                    ) : (
                      <select value={app.status === 'under_review' ? 'received' : app.status}
                        onChange={e => onStatusChange(app.id, e.target.value)}
                        className={`border rounded-lg px-2 py-1 text-[11px] font-medium focus:outline-none focus:border-primary cursor-pointer ${app._stage.color} ${app._stage.text}`}
                        data-testid={`row-status-${app.id}`}>
                        {STAGES.map(st => <option key={st.key} value={st.key}>{st.label}</option>)}
                      </select>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {app.decision_notification?.sent && app.decision_notification.category === app.status ? (
                      <span className={`inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium ${app._stage.text}`}
                        data-testid={`table-notified-${app.id}`}
                        title={app.decision_notification.sent_at ? `Email sent ${new Date(app.decision_notification.sent_at).toLocaleString()}` : 'Current outcome emailed'}>
                        <EnvelopeSimple size={13} /><Check size={11} />
                        {app.status === 'accepted' ? 'Acceptance sent' : app.status === 'rejected' ? 'Rejection sent' : 'Waitlist sent'}
                      </span>
                    ) : <span className="text-[11px] text-text-muted">{CLOSE_EMAIL_CATEGORIES.includes(app.status) ? 'Not notified' : 'Awaiting decision'}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Three-step "Close & Convert" modal — strong friction so users don't accidentally
// close. Step 1 picks the portfolio fund, step 2 is where the creator writes the
// decision email each outcome group receives (previously notified outcomes
// are skipped — kanban moves are silent), step 3 is the typed confirmation.
const CloseProgramModal = ({ program, acceptedCount, fullProfileCount, quickProfileCount, onClose, onClosed, noun }) => {
  const [step, setStep] = useState(1);
  const [fundChoice, setFundChoice] = useState('new');
  const [existingFunds, setExistingFunds] = useState([]);
  const [chosenFundId, setChosenFundId] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [includeReceived, setIncludeReceived] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const { loading: draftsLoading, drafts, setDrafts, meta } = useCloseEmailDrafts(program.id);

  useEffect(() => {
    api.get('/vc/managed-funds')
      .then(r => { setExistingFunds(r.data || []); })
      .catch(() => {});
  }, []);

  // Every applicant who will get mail when we close — drives the confirm-step
  // summary so nobody discovers the blast radius after the fact.
  const emailTally = CLOSE_EMAIL_CATEGORIES.map(k => ({
    key: k,
    label: drafts?.[k]?.label || k,
    count: drafts?.[k]?.enabled ? (drafts[k].recipientCount || 0) : 0,
    off: drafts?.[k] ? (!drafts[k].enabled && drafts[k].recipientCount > 0) : false,
    total: drafts?.[k]?.recipientCount || 0,
  }));
  const neutralCount = includeReceived ? 0 : (meta.receivedCount || 0);
  const emailTotal = emailTally.reduce((n, t) => n + t.count, 0) + neutralCount;

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        fund_choice: fundChoice,
        managed_fund_id: fundChoice === 'existing' ? chosenFundId : null,
        confirm: true,
        emails: drafts ? toEmailPayload(drafts) : undefined,
        include_received_in_rejected: includeReceived,
      };
      const res = await api.post(`/vc/programs/${program.id}/close`, payload);
      const entries = res.data.full_profiles_added + res.data.stub_profiles_created;
      const msg = !res.data.managed_fund_id
        ? 'Closed. No accepted applicants, so nothing moved to your portfolio.'
        : res.data.managed_fund_was_created
          ? `Closed. Created fund "${res.data.managed_fund_name}" with ${entries} portfolio entries.`
          : `Closed. Added ${entries} entries to "${res.data.managed_fund_name}".`;
      const mailed = res.data.emails_total || 0;
      toast.success(`${msg} Sending ${mailed} email${mailed === 1 ? '' : 's'}.`);
      onClosed();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to close');
      setSubmitting(false);
    }
  };

  const requiredText = `close ${program.name}`;
  const canSubmit = step === 3 && confirmText.trim().toLowerCase() === requiredText.toLowerCase() && (fundChoice === 'new' || (fundChoice === 'existing' && chosenFundId));

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className={`bg-surface rounded-xl border border-border w-full ${step === 2 ? 'max-w-2xl' : 'max-w-lg'} p-6 max-h-[92vh] overflow-y-auto`} data-testid="close-program-modal">
        {/* Step rail */}
        <div className="flex items-center gap-1.5 mb-4">
          {['Portfolio', 'Emails', 'Confirm'].map((label, i) => (
            <div key={label} className="flex items-center gap-1.5 flex-1">
              <div className={`h-1 flex-1 rounded-full ${step > i ? 'bg-primary' : 'bg-border'}`} />
              <span className={`text-[10px] uppercase tracking-wider font-semibold ${step === i + 1 ? 'text-primary' : 'text-text-muted'}`}>{label}</span>
            </div>
          ))}
        </div>
        {step === 1 ? (
          <>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-full bg-warning/15 flex items-center justify-center">
                <Buildings size={18} weight="fill" className="text-warning" />
              </div>
              <div>
                <h2 className="text-lg font-heading font-bold text-text-primary">Close {noun.toLowerCase()} & convert to portfolio</h2>
                <div className="text-xs text-text-secondary">This is permanent. Closed {noun.toLowerCase()}s cannot be reopened.</div>
              </div>
            </div>

            <div className="bg-background rounded-lg border border-border p-4 mb-5">
              <div className="text-xs text-text-secondary mb-2">When you close <strong className="text-text-primary">{program.name}</strong>:</div>
              <ul className="text-sm text-text-primary space-y-2">
                {acceptedCount > 0 ? (
                  <>
                    <li className="flex items-start gap-2"><Check size={14} weight="bold" className="text-success mt-0.5 flex-shrink-0" /> All <strong>{acceptedCount}</strong> accepted applicant{acceptedCount === 1 ? '' : 's'} are added to a portfolio fund.</li>
                    <li className="flex items-start gap-2"><Check size={14} weight="bold" className="text-success mt-0.5 flex-shrink-0" /> <strong>{fullProfileCount}</strong> Full profile{fullProfileCount === 1 ? '' : 's'} link directly. <strong>{quickProfileCount}</strong> Quick profile{quickProfileCount === 1 ? '' : 's'} create stub records (founders can claim later).</li>
                  </>
                ) : (
                  <li className="flex items-start gap-2"><Check size={14} weight="bold" className="text-text-muted mt-0.5 flex-shrink-0" /> No accepted applicants — nothing moves to your portfolio and no fund is created.</li>
                )}
                <li className="flex items-start gap-2"><Check size={14} weight="bold" className="text-success mt-0.5 flex-shrink-0" /> Applicants with an unsent outcome get the decision email you write on the next step.</li>
                <li className="flex items-start gap-2"><Check size={14} weight="bold" className="text-success mt-0.5 flex-shrink-0" /> Public apply page locks — no new applications.</li>
                <li className="flex items-start gap-2"><Check size={14} weight="bold" className="text-success mt-0.5 flex-shrink-0" /> All applications stay visible (history preserved).</li>
              </ul>
            </div>

            {acceptedCount > 0 && (
            <>
            <label className="block text-xs uppercase tracking-wider text-text-secondary mb-3 font-semibold">Where should accepted startups land?</label>
            <div className="space-y-2 mb-5">
              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${fundChoice === 'new' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`} data-testid="fund-choice-new">
                <input type="radio" checked={fundChoice === 'new'} onChange={() => setFundChoice('new')} className="mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-text-primary">Create a new fund named "<span className="text-primary">{program.name}</span>"</div>
                  <div className="text-xs text-text-secondary mt-0.5">Recommended when this program represents a discrete cohort or thesis.</div>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${fundChoice === 'existing' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`} data-testid="fund-choice-existing">
                <input type="radio" checked={fundChoice === 'existing'} onChange={() => setFundChoice('existing')} className="mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-text-primary">Add to an existing managed fund</div>
                  {fundChoice === 'existing' && (
                    <select value={chosenFundId} onChange={e => setChosenFundId(e.target.value)}
                      className="mt-2 w-full bg-background border border-border rounded px-2 py-1.5 text-sm text-text-primary focus:outline-none focus:border-primary"
                      data-testid="existing-fund-select">
                      <option value="">— Select fund —</option>
                      {existingFunds.map(f => (<option key={f.id} value={f.id}>{f.name} {f.vintage_year ? `· ${f.vintage_year}` : ''}</option>))}
                    </select>
                  )}
                  {fundChoice !== 'existing' && existingFunds.length === 0 && <div className="text-xs text-text-muted mt-0.5">You haven't created any managed funds yet.</div>}
                </div>
              </label>
            </div>
            </>
            )}

            <div className="flex gap-3">
              <button onClick={onClose} className="flex-1 py-2.5 bg-background border border-border text-text-primary rounded-lg font-medium hover:border-primary">Cancel</button>
              <button onClick={() => setStep(2)} disabled={acceptedCount > 0 && fundChoice === 'existing' && !chosenFundId} className="flex-1 py-2.5 bg-warning text-black rounded-lg font-semibold disabled:opacity-50" data-testid="close-modal-continue">
                Continue
              </button>
            </div>
          </>
        ) : step === 2 ? (
          <>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center">
                <EnvelopeSimple size={18} weight="fill" className="text-primary" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-heading font-bold text-text-primary">Write the emails applicants receive</h2>
                <div className="text-xs text-text-secondary">
                  Nothing was emailed while you moved cards. Everyone hears their outcome when you close — from the copy you write here.
                </div>
              </div>
            </div>

            <div className="mt-4">
              {draftsLoading || !drafts ? (
                <div className="py-20 text-center text-sm text-text-muted">Loading email drafts…</div>
              ) : (
                <CloseEmailComposer
                  drafts={drafts}
                  setDrafts={setDrafts}
                  tokens={meta.tokens}
                  receivedCount={meta.receivedCount}
                  includeReceived={includeReceived}
                  setIncludeReceived={setIncludeReceived}
                />
              )}
            </div>

            <div className="flex gap-3 mt-5">
              <button onClick={() => setStep(1)} className="px-4 py-2.5 bg-background border border-border text-text-primary rounded-lg font-medium hover:border-primary">Back</button>
              <button
                onClick={() => setShowPreview(true)}
                disabled={!drafts}
                className="flex items-center gap-1.5 px-4 py-2.5 border border-primary text-primary rounded-lg font-semibold hover:bg-primary/10 disabled:opacity-40"
                data-testid="close-modal-preview"
              >
                <Eye size={14} weight="bold" /> Preview emails
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={!drafts}
                className="flex-1 py-2.5 bg-warning text-black rounded-lg font-semibold disabled:opacity-40"
                data-testid="close-modal-continue-2"
              >
                Continue
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-full bg-danger/15 flex items-center justify-center">
                <Buildings size={18} weight="fill" className="text-danger" />
              </div>
              <div>
                <h2 className="text-lg font-heading font-bold text-text-primary">Final confirmation</h2>
                <div className="text-xs text-text-secondary">Type the phrase below to confirm. This action cannot be undone.</div>
              </div>
            </div>

            <div className="bg-danger/5 border border-danger/30 rounded-lg p-4 mb-4">
              <div className="text-xs uppercase tracking-wider text-danger font-semibold mb-1">Why we ask twice</div>
              <div className="text-xs text-text-secondary">
                Closing graduates this program into a permanent portfolio entry. The associated managed fund stays — you can edit valuations, equity, and metrics later — but the program itself locks. <strong className="text-text-primary">There is no Undo.</strong>
              </div>
            </div>

            {/* Mail blast radius — the last thing you see before the point of no return. */}
            <div className="bg-background border border-border rounded-lg p-4 mb-4" data-testid="close-email-summary">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider text-text-secondary font-semibold">Emails sent on close</div>
                <button onClick={() => setShowPreview(true)} disabled={!drafts}
                  className="flex items-center gap-1 text-[11px] text-primary hover:underline disabled:opacity-40" data-testid="close-confirm-preview">
                  <Eye size={12} weight="bold" /> Preview
                </button>
              </div>
              <ul className="space-y-1">
                {emailTally.map(t => (
                  <li key={t.key} className="flex items-center justify-between text-xs">
                    <span className="text-text-secondary">{t.label}</span>
                    <span className={t.off ? 'text-text-muted' : 'text-text-primary font-semibold'}>
                      {t.off ? `${t.total} — turned off` : `${t.count} email${t.count === 1 ? '' : 's'}`}
                    </span>
                  </li>
                ))}
                {neutralCount > 0 && (
                  <li className="flex items-center justify-between text-xs">
                    <span className="text-text-secondary">Untriaged (neutral closure note)</span>
                    <span className="text-text-primary font-semibold">{neutralCount} email{neutralCount === 1 ? '' : 's'}</span>
                  </li>
                )}
              </ul>
              <div className="mt-2 pt-2 border-t border-border flex items-center justify-between text-xs">
                <span className="text-text-secondary font-semibold">Total</span>
                <span className="text-primary font-bold">{emailTotal}</span>
              </div>
            </div>

            <label className="block text-xs uppercase tracking-wider text-text-secondary mb-2 font-semibold">Type "<span className="text-text-primary lowercase">{requiredText}</span>" to confirm</label>
            <input type="text" value={confirmText} onChange={e => setConfirmText(e.target.value)}
              placeholder={requiredText}
              className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-danger mb-5"
              data-testid="close-confirm-input"
              autoFocus />

            <div className="flex gap-3">
              <button onClick={() => setStep(2)} className="flex-1 py-2.5 bg-background border border-border text-text-primary rounded-lg font-medium hover:border-primary">Back</button>
              <button onClick={submit} disabled={!canSubmit || submitting} className="flex-1 py-2.5 bg-danger text-white rounded-lg font-semibold disabled:opacity-40" data-testid="close-confirm-btn">
                {submitting ? 'Closing...' : `Close ${noun.toLowerCase()} & send ${emailTotal} email${emailTotal === 1 ? '' : 's'}`}
              </button>
            </div>
          </>
        )}
      </div>

      {showPreview && drafts && (
        <CloseEmailPreview
          programId={program.id}
          drafts={drafts}
          includeReceived={includeReceived}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
};

// A single workspace header keeps review controls separate from program management.
const EvaluationHeader = ({program,total,shown,search,onSearch,onBack,onCopy,onDownload,onCloseProgram,downloading,deleting,view,onView,toolbar,onToolbar,filterCount,sorted,onReset,onEvaluate,bulkEval,actions}) => {
  const menu=useRef(null);
  useEffect(()=>{
    const outside=event=>{if(menu.current&&!menu.current.contains(event.target))menu.current.open=false;};
    const measure=()=>{if(menu.current)menu.current.style.setProperty('--ie-menu-space',`${Math.max(100,(window.visualViewport?.height||innerHeight)-menu.current.getBoundingClientRect().bottom-16)}px`);};
    menu.current?.addEventListener('toggle',measure);window.addEventListener('resize',measure);window.visualViewport?.addEventListener('resize',measure);
    document.addEventListener('pointerdown',outside);
    return()=>{document.removeEventListener('pointerdown',outside);menu.current?.removeEventListener('toggle',measure);window.removeEventListener('resize',measure);window.visualViewport?.removeEventListener('resize',measure);};
  },[]);
  const until=program.open_until&&new Date(program.open_until);
  const date=until&&!Number.isNaN(until.getTime())?until.toLocaleDateString('en-IN',{day:'numeric',month:'short',timeZone:'Asia/Kolkata'}):null;
  return <header className="ie-header" data-testid="pipeline-sticky-header">
    <div className="ie-program-line">
      <div className="ie-program-identity"><button className="ie-back" onClick={onBack} aria-label="Back to programs"><ArrowLeft size={18}/></button><div><h1 data-testid="selected-program-name">{program.name}</h1><p><span className={`ie-status is-${program.status}`}>{program.historical_quick_apply_import?'Uploaded':program.status||'Draft'}</span>{date&&<span>{program.status==='closed'?'Ended':'Closes'} {date}</span>}{program.converted_managed_fund_name&&<span>In {program.converted_managed_fund_name}</span>}</p></div></div>
      <div className="ie-utilities">
        {!program.historical_quick_apply_import&&program.slug&&<button onClick={onCopy} data-testid="header-copy-link"><Copy size={16}/><span>Copy link</span></button>}
        <button onClick={onDownload} disabled={downloading} data-testid="header-download"><DownloadSimple size={16}/><span>{downloading?'Downloading…':'Download'}</span></button>
        {program.status!=='closed'&&<button className="ie-close-program" onClick={onCloseProgram} disabled={deleting} data-testid="close-program-btn"><Check size={16}/><span>Close program</span></button>}
        <details ref={menu} className="ie-menu" onKeyDown={event=>{if(event.key==='Escape'){event.stopPropagation();menu.current.open=false;menu.current.querySelector('summary').focus();}}}>
          <summary aria-label="Program actions" title="Program actions"><DotsThree size={22} weight="bold"/></summary>
          <div className="ie-menu-panel"><div className="ie-menu-group em-mobile-actions"><small>Program actions</small>{!program.historical_quick_apply_import&&program.slug&&<button onClick={()=>{menu.current.open=false;onCopy();}}><Copy size={16}/>Copy application link</button>}<button disabled={downloading} onClick={event=>{menu.current.open=false;onDownload(event);}}><DownloadSimple size={16}/>{downloading?'Downloading…':'Download applications'}</button>{program.status!=='closed'&&<button disabled={deleting} onClick={()=>{menu.current.open=false;onCloseProgram();}}><Check size={16}/>Close program</button>}<button disabled={bulkEval.running||!total} onClick={()=>{menu.current.open=false;onEvaluate();}}><Sparkle size={16}/>{bulkEval.running?`${bulkEval.done}/${bulkEval.total} evaluated`:'Evaluate with AI'}</button></div>{actions.map(group=><div className="ie-menu-group" key={group.label}><small>{group.label}</small>{group.items.map(action=><button key={action.label} disabled={action.disabled||deleting} className={action.danger?'is-danger':''} data-testid={action.testId} onClick={()=>{menu.current.open=false;action.run();}}><action.icon size={16}/>{action.label}{action.count!=null&&<em>{action.count}</em>}</button>)}</div>)}</div>
        </details>
      </div>
    </div>
    <div className="ie-review-line" data-testid="pipeline-organized-toolbar">
      <div className="ie-review-title"><strong>Applicants <span>{shown===total?total:`${shown} / ${total}`}</span></strong><label className="ie-search"><MagnifyingGlass size={15}/><input aria-label="Search applicants" placeholder="Search applicants" value={search} onChange={event=>onSearch(event.target.value)}/>{search&&<button onClick={()=>onSearch('')} aria-label="Clear applicant search"><X size={13}/></button>}</label></div>
      <div className="ie-review-tools">
        <div className="ie-view" role="group" aria-label="Applicant view">{[{key:'kanban',label:'Board',icon:SquaresFour},{key:'table',label:'Table',icon:List}].map(item=><button key={item.key} aria-label={`${item.label} view`} title={`${item.label} view`} aria-pressed={view===item.key} data-testid={`view-${item.key}`} onClick={()=>onView(item.key)}><item.icon size={17}/></button>)}</div>
        <button className={toolbar==='filter'||filterCount?'is-active':''} aria-expanded={toolbar==='filter'} data-testid="pipeline-filter-button" onClick={()=>onToolbar(toolbar==='filter'?null:'filter')}><FunnelSimple size={15}/>Filter{filterCount>0&&<b>{filterCount}</b>}</button>
        <button className={toolbar==='sort'||sorted?'is-active':''} aria-expanded={toolbar==='sort'} data-testid="pipeline-sort-button" onClick={()=>onToolbar(toolbar==='sort'?null:'sort')}><ArrowsDownUp size={15}/>Sort</button>
        {(filterCount>0||sorted)&&<button className="ie-reset" onClick={onReset} title="Reset filters and sorting" aria-label="Reset filters and sorting"><X size={14}/></button>}
        <button className="ie-ai" data-testid="ai-evaluate-all-btn" disabled={bulkEval.running||!total} onClick={onEvaluate}><Sparkle size={16}/>{bulkEval.running?`${bulkEval.done}/${bulkEval.total} evaluated`:'Evaluate with AI'}</button>
      </div>
    </div>
  </header>;
};

const VCPrograms = ({ user, entityType }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const mobileEvaluation=useMobileEvaluation();
  const [mobileStage,setMobileStage]=useState('received');
  const [evaluating,setEvaluating] = useState(()=>!!new URLSearchParams(location.search).get('program'));
  const [applicationError,setApplicationError] = useState('');
  const applicationRequest = useRef(0);
  const [applicantSearch,setApplicantSearch] = useState('');
  const isIncubator = entityType === 'incubator';
  const [programStatus, setProgramStatus] = useState('all');
  const visiblePrograms = programs => programs.filter(p => (programStatus === 'all' || p.status === programStatus));
  const noun = isIncubator ? 'Program' : 'Watchlist';
  const nounPlural = isIncubator ? 'Programs' : 'Watchlists';

  const [programs, setPrograms] = useState([]);
  const [programError,setProgramError] = useState('');
  const [selectedProgramId, setSelectedProgramId] = useState(null);
  const appliedProgramLink = useRef(null);
  useEffect(() => {
    const requested = new URLSearchParams(location.search).get('program');
    if (!requested) { appliedProgramLink.current = null; return; }
    if (requested === appliedProgramLink.current) return;
    if (programs.some(program => program.id === requested)) { appliedProgramLink.current = requested; setSelectedProgramId(requested); setEvaluating(true); }
  }, [location.search, programs]);
  const [program, setProgram] = useState(null);
  const [grouped, setGrouped] = useState({ received: [], accepted: [], rejected: [], waitlisted: [] });
  const [total, setTotal] = useState(0);
  const [loadingProgs, setLoadingProgs] = useState(true);
  const [loadingApps, setLoadingApps] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showPortfolioDialog, setShowPortfolioDialog] = useState(false);
  const [invitingFounders, setInvitingFounders] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [showClassicBulk, setShowClassicBulk] = useState(false);
  const [editing, setEditing] = useState(null);
  const appliedApplicationLink = useRef(null);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requested = params.get('application'), requestedProgram = params.get('program');
    const link = `${requestedProgram}:${requested}`;
    if (!requested) { appliedApplicationLink.current = null; return; }
    if (appliedApplicationLink.current === link || selectedProgramId !== requestedProgram) return;
    if (Object.values(grouped).flat().some(application => application.id === requested)) {
      appliedApplicationLink.current = link;
      navigate(`/vc/company/${encodeURIComponent('application:'+requested)}?program=${encodeURIComponent(requestedProgram)}&application=${encodeURIComponent(requested)}&section=application`, {replace:true,state:{from:`/vc/programs?program=${encodeURIComponent(requestedProgram)}`,companyBackground:{pathname:'/vc/programs',search:`?program=${encodeURIComponent(requestedProgram)}`,hash:''},companyOriginId:`app-card-${requested}`,companyReturnByBack:false}});
    }
  }, [location.search, selectedProgramId, grouped, navigate]);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('vc_programs_view') || 'kanban');
  // Pipeline filters/sort shared by board + table; remembered per browser like the view mode.
  const [scoreView, setScoreViewState] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('vc_programs_score_view') || '{}');
      // Migrate the previous score-only sorter into the new pipeline sorter.
      if (saved.sort === 'desc') saved.sort = 'ai_desc';
      if (saved.sort === 'asc') saved.sort = 'ai_asc';
      if (saved.sort === 'none') saved.sort = 'default';
      if (saved.sort?.startsWith('badge:')) saved.sort = 'default';
      delete saved.profile;
      if (!STARTUP_BADGES.some(badge => badge.id === saved.badge)) saved.badge = 'all';
      return { ...DEFAULT_SCORE_VIEW, ...saved };
    } catch { return DEFAULT_SCORE_VIEW; }
  });
  const setScoreView = (v) => {
    setScoreViewState(v);
    try { localStorage.setItem('vc_programs_score_view', JSON.stringify(v)); } catch { /* storage blocked — fine */ }
  };
  const [pipelineToolbar, setPipelineToolbar] = useState(null); // null | 'filter' | 'sort'
  const [activeDragId, setActiveDragId] = useState(null);
  const activeDragWidth=useRef(260);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showDecisionEmails, setShowDecisionEmails] = useState(false);
  const [bulkEval, setBulkEval] = useState({ running: false, total: 0, done: 0, failed: 0 });
  const prevBulkRunningRef = useRef(false);
  const carouselRef = useRef(null);
  const [topOffset, setTopOffset] = useState(0);
  const boardHeadings=useRef(null),boardBody=useRef(null);
  const syncBoardScroll=(event,target)=>{if(target.current&&Math.abs(target.current.scrollLeft-event.currentTarget.scrollLeft)>.5)target.current.scrollLeft=event.currentTarget.scrollLeft;};

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  // Legacy VC workspace keeps its viewport panel. Incubator reviews scroll as a page.
  useEffect(() => {
    if(isIncubator)return undefined;
    let rafId;
    const tick=()=>{const main=document.querySelector('main');if(main){const next=Math.round(main.getBoundingClientRect().top);setTopOffset(prev=>prev===next?prev:next);}rafId=requestAnimationFrame(tick);};
    rafId=requestAnimationFrame(tick);
    return()=>cancelAnimationFrame(rafId);
  },[isIncubator]);

  useEffect(() => { fetchPrograms(); }, []);
  useEffect(() => {
    if (selectedProgramId) {
      fetchApplications(selectedProgramId);
      
      // Resume the progress pill if a bulk run was already in flight (e.g. after a page reload).
      api.get(`/vc/programs/${selectedProgramId}/ai-evaluate-status`)
        .then(res => setBulkEval(res.data))
        .catch(() => setBulkEval({ running: false, total: 0, done: 0, failed: 0 }));
    }
  }, [selectedProgramId]);

  // Poll bulk AI-evaluation progress every 2s while a run is active for the selected program.
  useEffect(() => {
    if (!bulkEval.running || !selectedProgramId) return;
    const id = setInterval(async () => {
      try {
        const res = await api.get(`/vc/programs/${selectedProgramId}/ai-evaluate-status`);
        setBulkEval(res.data);
        fetchApplications(selectedProgramId, true);
        if (!res.data.running) clearInterval(id);
      } catch { /* try again next tick */ }
    }, 2000);
    return () => clearInterval(id);
  }, [bulkEval.running, selectedProgramId]);

  useEffect(() => {
    if (prevBulkRunningRef.current && !bulkEval.running && bulkEval.total > 0) {
      const succeeded = bulkEval.done - bulkEval.failed;
      toast.success(bulkEval.failed > 0
        ? `AI evaluated ${succeeded}/${bulkEval.total} applicants (${bulkEval.failed} failed)`
        : `AI evaluated ${bulkEval.total} applicant${bulkEval.total === 1 ? '' : 's'}`);
    }
    prevBulkRunningRef.current = bulkEval.running;
  }, [bulkEval.running, bulkEval.done, bulkEval.failed, bulkEval.total]);

  const startAiEvaluateAll = async () => {
    if (!program || total === 0) { toast.error('No applications to evaluate yet'); return; }
    if (total > 30 && !window.confirm(`Run AI evaluation on all ${total} applicants? This can take a few minutes.`)) return;
    try {
      const res = await api.post(`/vc/programs/${program.id}/ai-evaluate-all`);
      setBulkEval({ running: true, total: res.data.total, done: 0, failed: 0 });
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not start AI evaluation'); }
  };

  const [showAiPromptModal, setShowAiPromptModal] = useState(false);
  const [aiPromptDraft, setAiPromptDraft] = useState('');
  const [savingAiPrompt, setSavingAiPrompt] = useState(false);

  const openAiPromptModal = () => {
    setAiPromptDraft(program?.ai_eval_criteria || '');
    setShowAiPromptModal(true);
  };

  const saveAiPrompt = async () => {
    setSavingAiPrompt(true);
    try {
      const res = await api.patch(`/vc/programs/${program.id}`, { ai_eval_criteria: aiPromptDraft.trim() });
      setProgram(res.data);
      toast.success('AI evaluation prompt saved');
      setShowAiPromptModal(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save prompt'); }
    finally { setSavingAiPrompt(false); }
  };

  useEffect(()=>{
    const refresh=event=>{if(event.detail?.programId===selectedProgramId)fetchApplications(selectedProgramId,true);};
    window.addEventListener('fb-program-application-updated',refresh);
    return()=>window.removeEventListener('fb-program-application-updated',refresh);
  },[selectedProgramId]);

  // Polling: refresh active program's applications every 10s while tab is visible (catches new applicants in real time)
  useEffect(() => {
    if (!selectedProgramId) return;
    const tick = () => {
      if (document.visibilityState === 'visible') fetchApplications(selectedProgramId, true);;
    };
    const id = setInterval(tick, 10000);
    return () => clearInterval(id);
  }, [selectedProgramId]);

  // Listen for AI action events (e.g. AI moved applicants → refresh kanban instantly)
  useEffect(() => {
    const handler = (e) => {
      if (!e.detail) return;
      if (e.detail.type === 'bulk-move-applications' && e.detail.program_id === selectedProgramId) {
        fetchApplications(selectedProgramId);
      }
    };
    window.addEventListener('ai-action-applied', handler);
    return () => window.removeEventListener('ai-action-applied', handler);
  }, [selectedProgramId]);

  const handleDragStart = (event) => {
    activeDragWidth.current=event.active.rect.current.initial?.width||260;
    setActiveDragId(event.active.id);
    
  };

  const handleDragEnd = async (event) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over) return;
    const newStage = String(over.id).replace(/^col-/, '');
    if (!STAGES.find(s => s.key === newStage)) return;
    
    // Find current stage of dragged app
    const dragged = Object.values(grouped).flat().find(a => a.id === active.id);
    if (!dragged || dragged.status === newStage) return;
    
    // Optimistic update: immediately move locally
    setGrouped(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(k => { next[k] = next[k].filter(a => a.id !== active.id); });
      next[newStage] = [...(next[newStage] || []), { ...dragged, status: newStage, decision_notification: null }];
      return next;
    });
    
    try {
      await api.patch(`/vc/programs/${selectedProgramId}/applications/${active.id}`, { status: newStage });
      toast.success(`Moved to ${STAGES.find(s => s.key === newStage)?.label}`);
    } catch (err) {
      toast.error('Failed to move');
      fetchApplications(selectedProgramId);
    }
  };

  const fetchPrograms = async () => {
    setLoadingProgs(true);
    setProgramError('');
    try {
      const res = await api.get('/vc/programs');
      setPrograms(res.data);
      if (res.data.length > 0 && !selectedProgramId) {
        const requested = new URLSearchParams(location.search).get('program');
        setSelectedProgramId(res.data.find(program => program.id === requested)?.id || res.data[0].id);
      }
    } catch (err) { setProgramError('Programs could not be loaded. Please retry.'); }
    finally { setLoadingProgs(false); }
  };

  const fetchApplications = async (pid, silent = false) => {
    const request=++applicationRequest.current;
    setApplicationError('');
    if (!silent) {
        setLoadingApps(true);
    }
    try {
      const [pRes, aRes] = await Promise.all([
        api.get(`/vc/programs/${pid}`),
        api.get(`/vc/programs/${pid}/applications`),
      ]);
      if(request !== applicationRequest.current) return;
      setProgram(pRes.data);
      // Filter out under_review (legacy) into received bucket
      const buckets = { received: [], accepted: [], rejected: [], waitlisted: [] };
      const src = aRes.data.applications_by_stage || {};
      Object.entries(src).forEach(([k, v]) => {
        if (k === 'under_review') buckets.received.push(...v);
        else if (buckets[k]) buckets[k] = v;
      });
      setGrouped(buckets);
      setTotal(aRes.data.total);
    } catch (err) { if(request===applicationRequest.current){setApplicationError('Could not load applications. Please retry.');toast.error('Failed to load');} }
    finally {
        if (!silent && request===applicationRequest.current) {
            setLoadingApps(false);
        }
    }
  };

  const handleCreate = async (payload) => {
    const res = await api.post('/vc/programs', payload);
    toast.success(`${noun} created!`);
    fetchPrograms();
    setSelectedProgramId(res.data.id);
  };

  const handleUpdate = async (payload) => {
    await api.patch(`/vc/programs/${editing.id}`, payload);
    toast.success(`${noun} updated`);
    fetchPrograms();
    if (selectedProgramId === editing.id) fetchApplications(editing.id);
  };

  const [deletingId, setDeletingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const handleDelete = async (p, e) => {
    e?.stopPropagation();
    if (deletingId) return;
    if (!confirm(`Delete "${p.name}"? This will remove all applications. Continue?`)) return;
    setDeletingId(p.id);
    try {
      await api.delete(`/vc/programs/${p.id}`);
      toast.success(`${noun} deleted`);
      if (selectedProgramId === p.id) {
        setSelectedProgramId(null);
        setProgram(null);
        if (isIncubator) {
          setEvaluating(false);
          const query = new URLSearchParams(location.search);
          query.delete("program"); query.delete("application");
          navigate(`${location.pathname}?${query}`, {replace:true});
        }
      }
      fetchPrograms();
    } catch { toast.error('Could not delete program. Try again.'); }
    finally { setDeletingId(null); }
  };

  const copyLink = async (slug, e) => {
    e?.stopPropagation();
    const link = `${window.location.origin}/api/share/program/${slug}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Application link copied');
    } catch { toast.error('Could not copy the link. Please allow clipboard access and try again.'); }
  };


  const downloadApplications = async (p, e) => {
    e?.stopPropagation();
    if (downloadingId) return;
    setDownloadingId(p.id);

    try {
        const response = await api.get(
        `/vc/programs/${p.id}/applications/export`,
        {
            responseType: 'blob',
        }
        );

    const disposition = response.headers?.['content-disposition'] || '';

    const filenameMatch = disposition.match(
      /filename="?([^";]+)"?/i
    );

    const filename =
      filenameMatch?.[1] ||
      `${p.name || 'program'}-applications.zip`;

    const blobUrl = window.URL.createObjectURL(response.data);

    const link = document.createElement('a');

    link.href = blobUrl;
    link.download = filename;

    document.body.appendChild(link);

    link.click();
    link.remove();

    window.URL.revokeObjectURL(blobUrl);

    toast.success('Application data downloaded');
  } catch (err) {
    console.error(
      'Failed to export applications',
      err
    );

    toast.error(
      err.response?.data?.detail ||
      'Failed to download application data'
    );
  } finally { setDownloadingId(null); }
};

  const handleAppClick = (appId,source) => navigate(`/vc/company/${encodeURIComponent('application:'+appId)}?program=${encodeURIComponent(selectedProgramId)}&application=${encodeURIComponent(appId)}&section=application`, {state:{from:`/vc/programs?program=${encodeURIComponent(selectedProgramId)}`,companyBackground:{pathname:location.pathname,search:`?program=${encodeURIComponent(selectedProgramId)}`,hash:location.hash||''},companyOriginId:source?.getAttribute('data-testid')||`app-card-${appId}`,companyReturnByBack:true,applicationReviewIds:(isIncubator&&mobileEvaluation?(viewGrouped[mobileStage]||[]):STAGES.flatMap(stage=>viewGrouped[stage.key]||[])).map(app=>app.id)}});


  const changeView = (mode) => {
    setViewMode(mode);
    localStorage.setItem('vc_programs_view', mode);
  };

  // Inline status change from the table's Status dropdown — same endpoint the
  // kanban drag uses, with the same optimistic local move.
  const handleStatusChange = async (appId, newStage) => {
    const current = Object.values(grouped).flat().find(a => a.id === appId);
    if (!current || current.status === newStage) return;
    setGrouped(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(k => { next[k] = next[k].filter(a => a.id !== appId); });
      next[newStage] = [...(next[newStage] || []), { ...current, status: newStage, decision_notification: null }];
      return next;
    });
    try {
      await api.patch(`/vc/programs/${selectedProgramId}/applications/${appId}`, { status: newStage });
      toast.success(`Moved to ${STAGES.find(s => s.key === newStage)?.label}`);
    } catch {
      toast.error('Failed to move');
      fetchApplications(selectedProgramId);
    }
  };


  const scrollCarousel = (dir) => {
    if (!carouselRef.current) return;
    const card = carouselRef.current.querySelector('[data-testid^="program-card-"]');
    const width = card?.getBoundingClientRect().width || 300;
    const gap = parseFloat(getComputedStyle(carouselRef.current).gap) || 14;
    carouselRef.current.scrollBy({ left: dir * (width + gap), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };

  const evalQuestions = getEvalQuestions(program);

  // Apply pipeline filters/sort to each stage. `grouped` remains the source of
  // truth for drag/drop and detail views; `viewGrouped` is presentation-only.
  const hasManual = evalQuestions.length > 0;
  const effectiveScoreView = hasManual ? scoreView : { ...scoreView, by: 'ai' };
  const pipelineSort = comparePipeline(effectiveScoreView.sort, hasManual);
  const viewGrouped = {};
  STAGES.forEach(stage => {
    const withScores = (grouped[stage.key] || []).map(a => ({ ...a, _scores: appScores(a, evalQuestions) }));
    const query=isIncubator?applicantSearch.trim().toLowerCase():'';
    const kept = withScores.filter(a => passesPipelineFilters(a, stage.key, effectiveScoreView) && (!query || [applicantName(a),a.applicant_name,a.applicant_email,a.imported_startup_name].some(value=>String(value||'').toLowerCase().includes(query))));
    viewGrouped[stage.key] = pipelineSort ? [...kept].sort(pipelineSort) : kept;
  });
  const shownCount = STAGES.reduce((n, stage) => n + viewGrouped[stage.key].length, 0);
  const pipelineFiltering = filterViewActive(effectiveScoreView);
  const filterCount = activeFilterCount(effectiveScoreView);

  return (
    <div className={isIncubator ? `inc-page inc-programs ${evaluating?'ie-workspace':''}` : "p-4 sm:p-6"}>
      <div className="max-w-7xl mx-auto w-full">
        {/* Header + Carousel */}
        {(!isIncubator || !evaluating) && <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 ${isIncubator ? 'inc-programs-heading' : ''}`}>
          <div>
            <>{isIncubator ? <StartupPageTitle icon={Buildings} data-testid="programs-heading">{nounPlural}</StartupPageTitle> : <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary" data-testid="programs-heading">{nounPlural}</h1>}</>
            <p className="text-text-secondary text-[13px] mt-1">{isIncubator ? 'Manage cohorts, review applications and keep founders informed.' : 'Run dealflow watchlists. Share apply links. Source startups directly.'}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setShowBulk(true)} data-testid="template-import-open"
              className="flex-1 sm:flex-none justify-center flex items-center gap-2 px-3 py-2.5 border border-border rounded-lg text-sm font-medium hover:border-primary hover:text-primary">
              <FileArrowDown size={14} weight="bold" /> Bring to Founderboard
            </button>
            <button onClick={() => setShowClassicBulk(true)} data-testid="bulk-upload-open"
              className="flex-1 sm:flex-none justify-center flex items-center gap-2 px-3 py-2.5 border border-border rounded-lg text-sm font-medium hover:border-primary hover:text-primary">
              <UploadSimple size={14} weight="bold" /> Bulk upload
            </button>
            <button onClick={() => setShowCreate(true)} className={`${isIncubator ? "inc-gold-button" : "bg-primary text-white hover:bg-primary/90"} flex-1 sm:flex-none justify-center flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium`} data-testid="create-program-btn">
              <Plus size={16} weight="bold" /> New {noun}
            </button>
          </div>
        </div>

        }
        {isIncubator && !evaluating && <>
          <div className="inc-program-summary" aria-label="Program overview">{[['Total programs',programs.length],['Open programs',programs.filter(p => p.status === 'open').length],['Drafts',programs.filter(p => p.status === 'draft').length],['Applications',programs.reduce((sum,p) => sum + (Number(p.application_count) || 0),0)]].map(([label,value]) => <div key={label}><span>{label}</span><strong>{loadingProgs ? '—' : value}</strong></div>)}</div>
          <div className="inc-program-collection-toolbar"><div role="group" aria-label="Filter programs by status">{[['all','All programs'],['open','Open'],['scheduled','Scheduled'],['draft','Draft'],['closed','Closed']].map(([value,label]) => <button key={value} aria-pressed={programStatus === value} onClick={() => setProgramStatus(value)}>{label}</button>)}</div></div>
        </>}

        {isIncubator && programError && <div className="ipc-evaluation-error" role="alert">{programError}<button onClick={fetchPrograms}>Retry</button></div>}
        {/* Programs Carousel */}
        {isIncubator ? (evaluating ? (!program || loadingApps && !program ? <button className="ipc-evaluation-return" onClick={()=>{setEvaluating(false);const q=new URLSearchParams(location.search);q.delete('program');q.delete('application');navigate(`${location.pathname}?${q}`,{replace:true});}}><CaretLeft size={16}/>All programs</button> : null) : loadingProgs ? <p role="status" className="text-sm py-6">Loading programs…</p> : <IncubatorProgramCards programs={visiblePrograms(programs)} selectedId={selectedProgramId} onCopyLink={copyLink} onDelete={handleDelete} deletingId={deletingId} onCreate={()=>setShowCreate(true)} onEdit={setEditing} onEvaluate={p=>{setProgram(null);setSelectedProgramId(p.id);setEvaluating(true);navigate(`/vc/programs?program=${encodeURIComponent(p.id)}`);if(selectedProgramId===p.id)fetchApplications(p.id);}}/>) : loadingProgs ? (
          <div className="text-text-muted text-sm py-6">Loading {nounPlural.toLowerCase()}...</div>
        ) : programs.length === 0 ? (
          <div className="bg-surface border border-dashed border-border rounded-xl p-12 text-center" data-testid="empty-state">
            <Buildings size={48} className="text-text-muted mx-auto mb-4" weight="thin" />
            <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">No {nounPlural.toLowerCase()} yet</h3>
            <p className="text-text-secondary text-sm mb-5">Create your first {noun.toLowerCase()} to get a public apply link.</p>
            <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90">
              <Plus size={14} weight="bold" /> Create {noun}
            </button>
          </div>
        ) : (
          <div className="relative mb-5">
            <button onClick={() => scrollCarousel(-1)} className="absolute -left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-surface border border-border rounded-full flex items-center justify-center hover:bg-background shadow-sm" data-testid="carousel-prev"><CaretLeft size={14} /></button>
            <button onClick={() => scrollCarousel(1)} className="absolute -right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-surface border border-border rounded-full flex items-center justify-center hover:bg-background shadow-sm" data-testid="carousel-next"><CaretRight size={14} /></button>
            <div ref={carouselRef} className="flex gap-3 overflow-x-auto no-scrollbar pb-2 px-7" data-testid="programs-carousel">
              {(isIncubator ? visiblePrograms(programs) : programs).map(p => {
                const active = selectedProgramId === p.id;
                return (
                  <div key={p.id} onClick={() => { setSelectedProgramId(p.id);  }} role="button" tabIndex={0}
                    onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setSelectedProgramId(p.id);  } }}
                    className={`flex-shrink-0 w-[280px] cursor-pointer text-left bg-surface border rounded-[10px] p-4 transition-all ${active ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-primary/30'}`}
                    data-testid={`program-card-${p.id}`} aria-pressed={active}>
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-heading font-bold text-text-primary text-sm truncate flex-1">{p.name}</h3>
                      <span className={`text-[9px] px-2 py-0.5 rounded-full font-medium uppercase tracking-wider flex-shrink-0 ml-2 ${
                        p.historical_quick_apply_import ? 'bg-primary/10 text-primary'
                        : p.status === 'open' ? 'bg-success/10 text-success'
                        : p.status === 'scheduled' ? 'bg-info/10 text-info'
                        : p.status === 'draft' ? 'bg-text-muted/10 text-text-muted'
                        : 'bg-warning/10 text-warning'
                      }`} data-testid={`program-status-badge-${p.id}`}>{p.historical_quick_apply_import ? 'uploaded' : p.status}</span>
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-text-muted mb-3">
                      <div className="flex items-center gap-1"><Users size={10} weight="bold" /> {p.application_count || 0}</div>
                      <div>{(p.questions || []).length} Q</div>
                    </div>
                    <div className="flex items-center gap-1.5 w-full">
  {!p.historical_quick_apply_import && (
    <button
      onClick={(e) => copyLink(p.slug, e)}
      className="px-2 py-1 text-[10px] font-medium text-text-secondary border border-border rounded hover:text-primary hover:border-primary/40 flex items-center gap-1"
      data-testid={`copy-link-${p.id}`}
      title="Copy apply link"
    >
      <Link size={10} /> Link
    </button>
  )}

  <button
    onClick={(e) => {
      e.stopPropagation();
      setEditing(p);
    }}
    className="px-2 py-1 text-[10px] text-text-secondary border border-border rounded hover:text-primary hover:border-primary/40"
    data-testid={`edit-${p.id}`}
    title="Edit"
  >
    <PencilSimple size={10} />
  </button>

  {!p.historical_quick_apply_import && (
    <button
      onClick={(e) => downloadApplications(p, e)}
      className="px-2 py-1 text-[10px] text-text-secondary border border-border rounded hover:text-primary hover:border-primary/40"
      data-testid={`export-${p.id}`}
      title="Download applications and files"
    >
      <DownloadSimple size={10} />
    </button>
  )}

  <button
    onClick={(e) => handleDelete(p, e)}
    className="ml-auto px-2 py-1 text-[10px] text-red-600 border border-red-300 rounded hover:bg-red-50 hover:text-red-700 hover:border-red-400"
    data-testid={`delete-${p.id}`}
    title="Delete"
  >
    <Trash size={10} />
  </button>
</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {isIncubator && !evaluating && programs.length > 0 && visiblePrograms(programs).length === 0 && <div className="inc-program-no-results"><MagnifyingGlass size={25} /><p>No programs match these filters.</p><button onClick={() => { setProgramStatus('all'); }}>Reset filters</button></div>}

        {/* Incubator reviews use the app page scroll and a sticky board heading rail. */}
        {isIncubator && evaluating && applicationError && <div className="ipc-evaluation-error" role="alert">{applicationError}<button onClick={()=>fetchApplications(selectedProgramId)}>Retry</button></div>}
        {isIncubator && evaluating && loadingApps && !program && <p role="status" className="py-6 text-sm">Loading applicants…</p>}
        {(!isIncubator || evaluating) && selectedProgramId && program?.id === selectedProgramId && (
          <div
            key={isIncubator?"evaluation":topOffset}
            className={`sticky z-30 ${isIncubator?'':'bg-background'} flex flex-col overflow-hidden pt-[18px] ${isIncubator ? 'ipc-evaluating-panel' : ''}`}
            style={isIncubator ? undefined : { top: topOffset, height: `calc(100vh - ${topOffset}px)` }}
            data-testid="pipeline-panel">
            {isIncubator ? <EvaluationHeader program={program} total={total} shown={shownCount} search={applicantSearch} onSearch={setApplicantSearch}
              onBack={()=>{setEvaluating(false);setApplicantSearch('');const q=new URLSearchParams(location.search);q.delete('program');q.delete('application');navigate(`${location.pathname}?${q}`,{replace:true});}}
              onCloseProgram={()=>setShowCloseModal(true)} onCopy={()=>copyLink(program.slug)} onDownload={event=>downloadApplications(program,event)} downloading={!!downloadingId} deleting={!!deletingId}
              view={viewMode} onView={changeView} toolbar={pipelineToolbar} onToolbar={setPipelineToolbar} filterCount={filterCount} sorted={sortViewActive(effectiveScoreView)}
              onReset={()=>{setScoreView({...DEFAULT_SCORE_VIEW});setPipelineToolbar(null);}} onEvaluate={startAiEvaluateAll} bulkEval={bulkEval}
              actions={[
                {label:'Program',items:[{label:'Edit program',icon:PencilSimple,run:()=>setEditing(program)},{label:'Import applicants',icon:UploadSimple,run:()=>setShowClassicBulk(true)},{label:'Bring to FounderBoard',icon:FileArrowDown,run:()=>setShowBulk(true)},...(program.historical_quick_apply_import?[{label:invitingFounders?'Queuing…':'Invite founders',icon:EnvelopeSimple,disabled:invitingFounders,run:async () => {
                  if (!window.confirm('Send secure profile claim invitations to new founders in this program? Existing accounts and founders with active invitations will be skipped. This does not send acceptance decisions.')) return;
                  setInvitingFounders(true);
                  try {
                    const { data } = await api.post(`/vc/programs/${program.id}/import-invitations`, { confirm: true });
                    toast.success(`${data.queued} invitation(s) queued · ${data.skipped} skipped`);
                    fetchApplications(program.id);
                  } catch (e) { toast.error(e.response?.data?.detail || 'Could not queue invitations'); }
                  finally { setInvitingFounders(false); }
                }}]:[])]},
                {label:'Evaluation',items:[{label:'AI instructions',icon:Gear,run:openAiPromptModal,testId:'ai-prompt-edit-btn'},{label:'Decision email templates',icon:EnvelopeSimple,run:()=>setShowDecisionEmails('edit'),testId:'edit-decision-emails-open'}]},
                {label:'Manage',items:[...(program.status==='closed'&&(grouped.accepted?.length||0)>0?[{label:program.converted_managed_fund_id?'Update portfolio':'Add to portfolio',icon:Vault,run:()=>setShowPortfolioDialog(true),testId:'convert-portfolio-btn'}]:[]),{label:deletingId?'Deleting…':'Delete program',icon:Trash,danger:true,run:()=>handleDelete(program),testId:'header-delete'}]}
              ]}/>
              : <div className="program-pipeline-header flex-shrink-0" data-testid="pipeline-sticky-header">
              <div className="program-pipeline-heading">
                <div className="program-pipeline-identity">
              <div>
                <h2 className="font-heading font-bold text-lg text-text-primary flex items-center gap-2" data-testid="selected-program-name">
                  {program.name}
                  {program.historical_quick_apply_import ? (
                    <span className="text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded bg-primary/10 text-primary" data-testid="program-uploaded-badge">Uploaded</span>
                  ) : program.status === 'closed' ? (
                    <span className="text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded bg-text-muted/10 text-text-muted" data-testid="program-closed-badge">Closed</span>
                  ) : null}
                  {!isIncubator && !program.historical_quick_apply_import && (
                    <button onClick={() => copyLink(program.slug)} title="Copy apply link"
                      className="flex items-center justify-center w-6 h-6 rounded-md text-text-muted hover:text-primary hover:bg-primary/10 transition-colors"
                      data-testid="header-copy-link">
                      <Copy size={13} weight="bold" />
                    </button>
                  )}
                </h2>
                <div className="text-[12px] text-text-muted">
                  {total} application{total === 1 ? '' : 's'} · /apply/{program.slug}
                  {program.status === 'closed' && program.closed_managed_fund_name && (
                    <> · Closed → <strong className="text-text-secondary">{program.closed_managed_fund_name}</strong></>
                  )}
                </div>
              </div>

                  <div className="program-pipeline-meta">                
                {program.status === 'closed' && program.converted_managed_fund_id && (
                  <span className="text-[11px] px-2.5 py-1 rounded bg-success/10 text-success font-semibold inline-flex items-center gap-1" data-testid="portfolio-converted-badge">
                    <Check size={10} weight="bold" /> In Portfolio · {program.converted_managed_fund_name}
                  </span>
                )}
</div>
                </div>
                <div className="program-lifecycle-actions" aria-label="Program actions">{isIncubator && <>
                  {!program.historical_quick_apply_import && program.slug && <button type="button" className="program-resource-action" onClick={()=>copyLink(program.slug)} data-testid="header-copy-link"><Copy size={17}/>Copy link</button>}
                  <button type="button" className="program-resource-action" disabled={!!downloadingId} onClick={event=>downloadApplications(program,event)} data-testid="header-download"><DownloadSimple size={17}/>{downloadingId===program.id?'Downloading…':'Download'}</button>
                  <button type="button" className="program-resource-action is-danger" disabled={!!deletingId} onClick={event=>handleDelete(program,event)} data-testid="header-delete"><Trash size={17}/>{deletingId===program.id?'Deleting…':'Delete'}</button>
                </>}                {program.historical_quick_apply_import && <button disabled={invitingFounders} onClick={async () => {
                  if (!window.confirm('Send secure profile claim invitations to new founders in this program? Existing accounts and founders with active invitations will be skipped. This does not send acceptance decisions.')) return;
                  setInvitingFounders(true);
                  try {
                    const { data } = await api.post(`/vc/programs/${program.id}/import-invitations`, { confirm: true });
                    toast.success(`${data.queued} invitation(s) queued · ${data.skipped} skipped`);
                    fetchApplications(program.id);
                  } catch (e) { toast.error(e.response?.data?.detail || 'Could not queue invitations'); }
                  finally { setInvitingFounders(false); }
                }} className="flex items-center gap-1.5 h-9 px-3 text-xs font-semibold text-primary border border-primary/30 rounded-lg hover:bg-primary/5 disabled:opacity-50" data-testid="invite-import-founders-btn"><EnvelopeSimple size={12} />{invitingFounders ? 'Queuing…' : 'Invite founders'}</button>}
                {program.status === 'closed' && (grouped.accepted?.length || 0) > 0 && <button onClick={() => setShowPortfolioDialog(true)} className="flex items-center gap-1.5 h-9 px-3 text-xs font-semibold text-primary border border-primary/30 rounded-lg hover:bg-primary/5" data-testid="convert-portfolio-btn"><Vault size={12} weight="bold" />{program.converted_managed_fund_id ? 'Update portfolio' : 'Add to portfolio'}</button>}
                {program.status !== 'closed' && (
                  <button onClick={() => setShowCloseModal(true)} className="flex items-center gap-1.5 h-9 px-3 text-xs font-semibold program-close-button border rounded-lg" data-testid="close-program-btn">
                    <Check size={12} weight="bold" /> Close program
                  </button>
                )}
</div>
              </div>
              <div className="program-pipeline-toolbar" data-testid="pipeline-organized-toolbar">
                <div className="program-view-controls" role="group" aria-label="Applicant view and filters">                {/* View switch and pipeline controls are intentionally separate, compact groups. */}
                <div className="flex items-center h-9 rounded-lg border border-border bg-transparent overflow-hidden" data-testid="view-toggle">
                  {[{ key: 'kanban', label: 'Board' }, { key: 'table', label: 'Table' }].map(v => (
                    <button key={v.key} onClick={() => changeView(v.key)} title={`${v.label} view`}
                      className={`h-9 px-3 text-xs font-semibold transition-colors ${viewMode === v.key ? 'bg-primary/10 text-primary' : 'text-text-muted hover:text-text-primary hover:bg-primary/5'}`}
                      data-testid={`view-${v.key}`}>
                      {v.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-1" data-testid="pipeline-controls">
                  <div className={`flex items-center h-9 rounded-lg border overflow-hidden transition-colors ${pipelineToolbar === 'filter' || pipelineFiltering ? 'border-primary/30' : 'border-border'}`}>
                    <button type="button" onClick={() => setPipelineToolbar(v => v === 'filter' ? null : 'filter')}
                      title={filterCount > 0 ? `Filter applications · ${filterCount} active` : 'Filter applications'}
                      className={`relative h-9 px-3 flex items-center justify-center gap-1.5 text-xs font-semibold transition-colors ${pipelineToolbar === 'filter' || pipelineFiltering ? 'text-primary bg-primary/5' : 'text-text-muted hover:text-text-primary hover:bg-primary/5'}`}
                      aria-expanded={pipelineToolbar === 'filter'} data-testid="pipeline-filter-button">
                      <FunnelSimple size={16} /> Filter
                      {filterCount > 0 && <span className="min-w-[16px] h-4 px-1 rounded-full bg-primary/10 text-primary text-[10px] leading-4 text-center font-semibold">{filterCount}</span>}
                    </button>
                    {pipelineFiltering && (
                      <button type="button"
                        onClick={() => { setScoreView({ ...effectiveScoreView, show: 'all', badge: 'all', age: 'all', stage: 'all' }); setPipelineToolbar(null); }}
                        className="h-9 px-2 border-l border-primary/20 text-[10px] font-semibold text-primary hover:bg-primary/5"
                        title="Reset filters" data-testid="pipeline-filter-button-reset">Reset</button>
                    )}
                  </div>
                  <div className={`flex items-center h-9 rounded-lg border overflow-hidden transition-colors ${pipelineToolbar === 'sort' || sortViewActive(effectiveScoreView) ? 'border-primary/30' : 'border-border'}`}>
                    <button type="button" onClick={() => setPipelineToolbar(v => v === 'sort' ? null : 'sort')}
                      title={sortViewActive(effectiveScoreView) ? 'Change sorting' : 'Sort applications'}
                      className={`relative h-9 px-3 flex items-center justify-center gap-1.5 text-xs font-semibold transition-colors ${pipelineToolbar === 'sort' || sortViewActive(effectiveScoreView) ? 'text-primary bg-primary/5' : 'text-text-muted hover:text-text-primary hover:bg-primary/5'}`}
                      aria-expanded={pipelineToolbar === 'sort'} data-testid="pipeline-sort-button">
                      <ArrowsDownUp size={16} /> Sort
                    </button>
                    {sortViewActive(effectiveScoreView) && (
                      <button type="button"
                        onClick={() => { setScoreView({ ...effectiveScoreView, sort: 'default' }); setPipelineToolbar(null); }}
                        className="h-9 px-2 border-l border-primary/20 text-[10px] font-semibold text-primary hover:bg-primary/5"
                        title="Reset sorting" data-testid="pipeline-sort-button-reset">Reset</button>
                    )}
                  </div>
                </div>
</div>
                <div className="program-review-controls" role="group" aria-label="Applicant review tools">                <div className="flex items-center rounded-lg border border-border overflow-hidden">
                  <button onClick={startAiEvaluateAll} disabled={bulkEval.running}
                    className="flex items-center gap-1.5 h-9 px-3 text-xs font-semibold text-text-secondary hover:bg-primary/5 hover:text-text-primary disabled:opacity-70"
                    data-testid="ai-evaluate-all-btn">
                    <Sparkle size={16} weight="bold" className={bulkEval.running ? 'animate-pulse' : ''} />
                    {bulkEval.running ? `Evaluating ${bulkEval.done}/${bulkEval.total}…` : 'AI Evaluation'}
                  </button>
                  <button onClick={openAiPromptModal} title="Edit AI evaluation prompt" aria-label="Edit AI evaluation prompt"
                    className="flex items-center justify-center w-9 h-9 text-text-muted border-l border-border hover:bg-primary/10"
                    data-testid="ai-prompt-edit-btn">
                    <Gear size={14} weight="bold" />
                  </button>
                </div>
                <button onClick={() => setShowDecisionEmails('edit')}
                  className="flex items-center gap-1.5 h-9 px-3 text-xs font-semibold text-text-secondary border border-border rounded-lg hover:border-primary/40 hover:text-primary"
                  data-testid="edit-decision-emails-open">
                  <EnvelopeSimple size={16} /> Edit decision emails
                </button>
</div>
              </div>
            </div>}


            {loadingApps ? (
              <div className="flex items-center justify-center py-16 text-text-muted">Loading pipeline...</div>
            ) : (
              <div className={isIncubator?'ie-review-content':'flex-1 min-h-0 flex flex-col overflow-hidden'}>
              {total > 0 && pipelineToolbar === 'filter' && (
                <PipelineFilterToolbar value={effectiveScoreView} onChange={setScoreView} hasManual={hasManual}
                  shown={shownCount} total={total} onClose={() => setPipelineToolbar(null)} />
              )}
              {total > 0 && pipelineToolbar === 'sort' && (
                <PipelineSortToolbar value={effectiveScoreView} onChange={setScoreView} hasManual={hasManual}
                  onClose={() => setPipelineToolbar(null)} />
              )}
              {!isIncubator && program.status === 'closed' && (
                <div className="flex-shrink-0 mb-3 px-3 py-2 rounded-lg bg-text-muted/5 border border-border flex items-center gap-2 text-[11px] text-text-secondary" data-testid="closed-program-banner">
                  <Check size={12} weight="bold" className="text-text-muted flex-shrink-0" />
                  <span><strong className="text-text-primary">This program is closed to new applicants.</strong> You can still triage, comment on and move existing applications — nothing here re-triggers decision emails.</span>
                </div>
              )}
              {isIncubator && total>0 && shownCount===0 && <div className="ie-no-results" role="status"><MagnifyingGlass size={22}/><strong>No matching applicants</strong><button onClick={()=>{setApplicantSearch('');setScoreView({...DEFAULT_SCORE_VIEW});setPipelineToolbar(null);}}>Clear search and filters</button></div>}
              {isIncubator&&mobileEvaluation ? <MobileApplicantList grouped={viewGrouped} stage={mobileStage} onStage={setMobileStage} questions={evalQuestions} onOpen={handleAppClick} onNotify={setShowDecisionEmails}/> : viewMode === 'table' ? (
                <ApplicationsTable
                  grouped={viewGrouped}
                  onRowClick={handleAppClick}
                  onStatusChange={handleStatusChange}
                  evalQuestions={evalQuestions}
                  sortRows={pipelineSort}
                  notificationGrouped={grouped}
                  compact={isIncubator}
                  onNotify={setShowDecisionEmails}
                />
              ) : (
              <div className={isIncubator?'ie-board-frame':'flex-1 min-h-0 flex flex-col overflow-hidden'}>
              {isIncubator&&<div className="ie-board-headings" ref={boardHeadings} onScroll={event=>syncBoardScroll(event,boardBody)} data-testid="board-lane-headers">
                {STAGES.map(stage=><div className="ie-lane ie-lane-header-cell" data-stage={stage.key} key={stage.key}><BoardLaneHeading stage={stage} count={(viewGrouped[stage.key]||[]).length} stageTotal={(grouped[stage.key]||[]).length} filtering={pipelineFiltering} incubator onNotify={setShowDecisionEmails} pendingNotifications={(grouped[stage.key]||[]).filter(a=>a.applicant_email?.trim()&&!(a.decision_notification?.sent&&a.decision_notification.category===a.status)).length}/></div>)}
              </div>}
              <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={()=>setActiveDragId(null)}>
                <div
                  className="flex gap-2 overflow-x-auto pb-2 flex-1 min-h-0 bg-background"
                  data-testid="kanban-board" ref={isIncubator?boardBody:undefined} onScroll={isIncubator?event=>syncBoardScroll(event,boardHeadings):undefined}>
                  {STAGES.map(stage => {
                    const items = viewGrouped[stage.key] || [];
                    const stageApps = grouped[stage.key] || [];
                    const stageTotal = stageApps.length;
                    const pendingNotifications = stageApps.filter(a => a.applicant_email?.trim() && !(a.decision_notification?.sent && a.decision_notification.category === a.status)).length;
                    return (
                      <DroppableColumn key={stage.key} stageKey={stage.key}
                        className={isIncubator?'ie-lane flex flex-col':'rounded-xl border border-border bg-surface flex flex-col overflow-hidden min-w-[260px] md:min-w-0 md:flex-1 h-full'}
                        data-testid={`stage-${stage.key}`} data-stage={stage.key}>

                        <div className={isIncubator?'ie-lane-scroll':'flex-1 min-h-0 overflow-y-auto'}>
                          {!isIncubator&&<BoardLaneHeading stage={stage} count={items.length} stageTotal={stageTotal} filtering={pipelineFiltering} onNotify={setShowDecisionEmails} pendingNotifications={pendingNotifications}/>}
                          {isIncubator&&items.length===0&&<p className="ie-lane-empty">{stageTotal?'No matches':'No applicants yet'}</p>}
                          <AnimatedPipelineList items={items} motionKey={JSON.stringify(effectiveScoreView)+applicantSearch}>
                            {app => (
                              <AppCard key={app.id} app={app}
                                onClick={event => handleAppClick(app.id,event.currentTarget)}
                                evalQuestions={evalQuestions} compact={isIncubator} />
                            )}
                          </AnimatedPipelineList>
                        </div>
                      </DroppableColumn>
                    );
                  })}
                </div>
                <DragOverlay dropAnimation={{ duration: 220 }}>
                  {activeDragId ? (() => {
                    const dragged = Object.values(grouped).flat().find(a => a.id === activeDragId);
                    if (!dragged) return null;
                    return isIncubator ? <div className="ie-drag-card" style={{width:activeDragWidth.current}} inert aria-hidden="true"><ApplicantCardProfile app={dragged} questions={evalQuestions}/></div> : (
                      <div className="bg-surface border border-primary ring-2 ring-primary/30 rounded-xl p-3 shadow-xl rotate-2" style={{ width: 240 }}>
                        <div className="text-xs font-medium text-text-primary truncate">{dragged.startup?.startup_name || dragged.applicant_name || dragged.applicant_email.split('@')[0]}</div>
                        <div className="text-[10px] text-text-muted truncate">{dragged.applicant_email}</div>
                      </div>
                    );
                  })() : null}
                </DragOverlay>
              </DndContext>
              </div>
              )}
              </div>
            )}
          </div>
        )}
      </div>

      {showAiPromptModal && program && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAiPromptModal(false)} data-testid="ai-prompt-modal">
          <div className="bg-surface border border-border rounded-xl w-[520px] max-w-full p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-base text-text-primary flex items-center gap-2">
                <Sparkle size={16} weight="bold" className="text-primary" /> AI evaluation prompt
              </h3>
              <button onClick={() => setShowAiPromptModal(false)} className="text-text-muted hover:text-text-primary"><X size={18} /></button>
            </div>
            <p className="text-[12px] text-text-muted mb-2">Your own instructions for the AI when it scores applicants to "{program.name}" — sector focus, traction bar, team profile, what to be skeptical of. Leave blank to let it infer fit from the program's description and questions.</p>
            <textarea value={aiPromptDraft} onChange={e => setAiPromptDraft(e.target.value)} rows={5} autoFocus
              placeholder="e.g. Prioritize technical founders in climate hardware with at least a working prototype. Be skeptical of pre-idea applicants."
              className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary resize-y"
              data-testid="ai-prompt-textarea" />
            <div className="flex items-center justify-end gap-2 mt-4">
              <button onClick={() => setShowAiPromptModal(false)} className="px-3 py-2 text-xs text-text-secondary hover:text-text-primary">Cancel</button>
              <button onClick={saveAiPrompt} disabled={savingAiPrompt}
                className="px-4 py-2 text-xs font-medium bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
                data-testid="ai-prompt-save-btn">
                {savingAiPrompt ? 'Saving…' : 'Save prompt'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreate && <ProgramFormModal onSave={handleCreate} onClose={() => setShowCreate(false)} isIncubator={isIncubator} />}
      {showPortfolioDialog && program && <ImportPortfolioDialog program={program} count={grouped.accepted?.length || 0} onClose={() => setShowPortfolioDialog(false)} onDone={() => { setShowPortfolioDialog(false); fetchPrograms(); fetchApplications(program.id); }} />}
      {showBulk && <BulkUploadPrograms onClose={() => setShowBulk(false)} onDone={() => fetchPrograms()} />}
      {showClassicBulk && <ClassicBulkUploadPrograms onClose={() => setShowClassicBulk(false)} onDone={() => fetchPrograms()} />}
      {editing && <ProgramFormModal initial={editing} onSave={handleUpdate} onClose={() => setEditing(null)} isIncubator={isIncubator} />}
      {showCloseModal && program && (
        <CloseProgramModal
          program={program}
          acceptedCount={grouped.accepted?.length || 0}
          fullProfileCount={grouped.accepted?.filter(a => a.startup_id).length || 0}
          quickProfileCount={grouped.accepted?.filter(a => !a.startup_id).length || 0}
          onClose={() => setShowCloseModal(false)}
          onClosed={() => { setShowCloseModal(false); fetchPrograms(); fetchApplications(program.id); }}
          noun={noun}
        />
      )}
      {showDecisionEmails && program && (
        <SendDecisionEmailsModal
          key={typeof showDecisionEmails === 'string' ? showDecisionEmails : 'all'}
          program={program}
          category={CLOSE_EMAIL_CATEGORIES.includes(showDecisionEmails) ? showDecisionEmails : null}
          editOnly={showDecisionEmails === 'edit'}
          onClose={() => setShowDecisionEmails(false)}
          onSent={() => { setShowDecisionEmails(false); fetchPrograms(); fetchApplications(program.id); }}
        />
      )}
    </div>
  );
};

export default VCPrograms;
